import { detectCapabilities, type CapabilityReport } from "../core/capabilities";
import { deviceLocale } from "../core/i18n";
import { Persistence } from "../core/persistence";
import { Router } from "../core/router";
import {
  DEFAULT_CLUB,
  DEFAULT_SETTINGS,
  type GameSettings,
  type GuestSession,
  type RouteId
} from "../core/types";
import {
  FORMATIONS,
  SQUAD_PLAYERS,
  TACTICAL_PRESETS,
  autoBuildLineup,
  benchPlayers,
  compatibleSwapTargets,
  createDefaultClubPlan,
  lineupPlayers,
  playerById,
  swapIntoLineup,
  teamRating,
  type ClubPlan,
  type FormationId,
  type TacticalPresetId
} from "../domain/club";
import type { MatchRenderer as MatchRendererInstance, RendererMetrics } from "../game/renderer";
import { MatchSimulation } from "../game/simulation";
import {
  EMPTY_INPUT,
  PITCH,
  type MatchConfig,
  type MatchInput,
  type MatchSnapshot,
  type MatchSummary
} from "../game/types";

interface PendingMatchOptions {
  opponent: "aurora" | "harbor" | "mountain";
  difficulty: MatchConfig["difficulty"];
  realDurationSeconds: number;
}

interface OpponentDefinition {
  name: string;
  shortName: string;
  color: string;
}

const OPPONENTS: Record<PendingMatchOptions["opponent"], OpponentDefinition> = {
  aurora: { name: "Aurora Athletic", shortName: "AUR", color: "#ff5c77" },
  harbor: { name: "Harbor City", shortName: "HBR", color: "#52a7ff" },
  mountain: { name: "Mountain Eleven", shortName: "MTN", color: "#ffb84c" }
};

const PLAYER_NAMES = SQUAD_PLAYERS.slice(0, 11).map(
  (player) => [String(player.number), player.shortName, player.position] as const
);

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function shortName(value: string): string {
  const letters = value
    .normalize("NFKC")
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
  return letters || "KIP";
}

function formatClock(seconds: number): string {
  const minute = Math.min(90, Math.floor(seconds / 60));
  const second = Math.floor(seconds % 60);
  return `${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
}

function percentage(home: number, away: number): [number, number] {
  const total = home + away;
  if (total <= 0) return [50, 50];
  const homePercent = Math.round((home / total) * 100);
  return [homePercent, 100 - homePercent];
}

type UiIcon =
  | "home"
  | "play"
  | "club"
  | "activities"
  | "more"
  | "players"
  | "profile"
  | "settings"
  | "arrow"
  | "ball"
  | "check"
  | "clock"
  | "device"
  | "history"
  | "shield"
  | "spark"
  | "stadium"
  | "tactics";

const UI_ICON_PATHS: Record<UiIcon, string> = {
  home:
    '<path d="M3.5 10.7 12 3.8l8.5 6.9v8.5a1.8 1.8 0 0 1-1.8 1.8H5.3a1.8 1.8 0 0 1-1.8-1.8z"/><path d="M9 21v-6h6v6"/>',
  play:
    '<path d="m8 5.2 10.5 6.3a.6.6 0 0 1 0 1L8 18.8a.6.6 0 0 1-.9-.5V5.7a.6.6 0 0 1 .9-.5Z"/><path d="M4 4v16"/>',
  club:
    '<path d="M12 2.8 20 6v5.5c0 4.8-3.2 8-8 9.7-4.8-1.7-8-4.9-8-9.7V6z"/><path d="m8.2 12 2.3 2.3 5.3-5.1"/>',
  activities:
    '<path d="M5 18.5V14l4-2.5 3 2 7-5"/><path d="M15 8.5h4v4"/><circle cx="5" cy="18.5" r="1.5"/><circle cx="9" cy="11.5" r="1.5"/>',
  more:
    '<circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none"/>',
  players:
    '<circle cx="9" cy="8" r="3"/><path d="M3.5 20v-2.2A4.8 4.8 0 0 1 8.3 13h1.4a4.8 4.8 0 0 1 4.8 4.8V20M15 5.5a3 3 0 0 1 0 5.8M16.8 14a4.2 4.2 0 0 1 3.7 4.2V20"/>',
  profile:
    '<circle cx="12" cy="8" r="3.4"/><path d="M5 21v-2.2A6.2 6.2 0 0 1 11.2 12h1.6a6.2 6.2 0 0 1 6.2 6.8V21"/>',
  settings:
    '<path d="M4 7h10M18 7h2M4 17h2M10 17h10M4 12h4M12 12h8"/><circle cx="16" cy="7" r="2"/><circle cx="8" cy="17" r="2"/><circle cx="10" cy="12" r="2"/>',
  arrow: '<path d="M5 12h14M14 7l5 5-5 5"/>',
  ball:
    '<circle cx="12" cy="12" r="9"/><path d="m9.2 9.1 2.8-2 2.8 2-1 3.3h-3.6zM7.2 15l3-2.6M16.8 15l-3-2.6M9.3 20l-2.1-5 1.1-3.8M14.7 20l2.1-5-1.1-3.8"/>',
  check: '<path d="m5 12.5 4.2 4.2L19 7"/>',
  clock:
    '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  device:
    '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 6h6M10 18h4"/>',
  history:
    '<path d="M4.5 7.5A9 9 0 1 1 3 12"/><path d="M4.5 3.8v3.7H8M12 7.5V12l3 1.8"/>',
  shield:
    '<path d="M12 2.8 20 6v5.5c0 4.8-3.2 8-8 9.7-4.8-1.7-8-4.9-8-9.7V6z"/>',
  spark:
    '<path d="m12 2 1.4 5.1L18 9.5l-4.6 2.4L12 17l-1.4-5.1L6 9.5l4.6-2.4z"/><path d="m18.5 15 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7z"/>',
  stadium:
    '<path d="M3 10.5 12 5l9 5.5V20H3z"/><path d="M7 20v-6h10v6M3 10.5h18M8 8.2v2.3M12 5v5.5M16 8.2v2.3"/>',
  tactics:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 4v16"/><circle cx="8" cy="9" r="1.5"/><circle cx="16" cy="15" r="1.5"/><path d="m8 10.5 1.8 3.2M16 13.5l-1.8-3.2"/>'
};

function uiIcon(name: UiIcon, className = "ui-icon"): string {
  return `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${UI_ICON_PATHS[name]}</svg>`;
}

export class KipsBallApp {
  private readonly router = new Router();
  private readonly persistence = new Persistence();
  private session: GuestSession | null = null;
  private settings: GameSettings = { ...DEFAULT_SETTINGS };
  private clubPlan: ClubPlan = createDefaultClubPlan();
  private capabilities: CapabilityReport | null = null;
  private activeMatch: MatchSnapshot | null = null;
  private history: MatchSummary[] = [];
  private lastSummary: MatchSummary | null = null;
  private lastRendererMetrics: RendererMetrics | null = null;
  private renderer: MatchRendererInstance | null = null;
  private simulation: MatchSimulation | null = null;
  private animationFrame = 0;
  private presentationTimer = 0;
  private cleanupHandlers: Array<() => void> = [];
  private matchPaused = false;
  private matchFinalizing = false;
  private playerSearch = "";
  private playerFilter = "ALL";
  private pendingMatch: PendingMatchOptions = {
    opponent: "aurora",
    difficulty: "amateur",
    realDurationSeconds: 240
  };

  constructor(private readonly root: HTMLElement) {}

  async start(): Promise<void> {
    await this.boot();
  }

  private async boot(): Promise<void> {
    const steps = [
      "Opening local game state",
      "Checking WebGL 2",
      "Validating Master Base Mesh",
      "Restoring match history"
    ];
    this.renderBoot(0, steps[0]!);

    try {
      await Promise.resolve();
      this.session = this.persistence.getSession();
      this.settings = this.persistence.getSettings();
      this.clubPlan = this.persistence.getClubPlan();
      this.renderBoot(0.25, steps[1]!);

      this.capabilities = detectCapabilities();
      if (!this.capabilities.webgl2) {
        this.renderBlockingError(
          "WebGL 2 is unavailable",
          "This device or browser cannot run the real 3D match renderer. KipsBall did not replace it with a fake 2D match.",
          () => void this.boot()
        );
        return;
      }

      this.renderBoot(0.5, steps[2]!);
      await this.validateMasterAsset();

      this.renderBoot(0.75, steps[3]!);
      [this.activeMatch, this.history] = await Promise.all([
        this.persistence.getActiveMatch(),
        this.persistence.getHistory()
      ]);

      this.renderBoot(1, "Ready");
      document.documentElement.classList.toggle("reduced-motion", this.settings.reducedMotion);
      await new Promise((resolve) => window.setTimeout(resolve, this.settings.reducedMotion ? 0 : 220));

      this.router.start((route) => void this.renderRoute(route));
      if (!this.session?.onboardingComplete) this.router.navigate("onboarding", true);
      else if (this.router.current() === "onboarding") this.router.navigate("home", true);
      else await this.renderRoute(this.router.current());

      this.registerServiceWorker();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown startup failure";
      this.renderBlockingError("KipsBall could not start", message, () => void this.boot());
    }
  }

  private async validateMasterAsset(): Promise<void> {
    const [assetResponse, metadataResponse] = await Promise.all([
      fetch("/assets/models/player-master-v1-1.glb", { cache: "force-cache" }),
      fetch("/assets/models/player-master-v1-1.license.json", { cache: "force-cache" })
    ]);
    if (!assetResponse.ok || !metadataResponse.ok) {
      throw new Error(
        `Master Base Mesh failed to load (${assetResponse.status}/${metadataResponse.status}).`
      );
    }
    const [buffer, metadata] = await Promise.all([
      assetResponse.arrayBuffer(),
      metadataResponse.json() as Promise<{ byteSize?: number; checksum?: string }>
    ]);
    const magic = new TextDecoder().decode(buffer.slice(0, 4));
    if (
      magic !== "glTF" ||
      buffer.byteLength < 1024 ||
      metadata.byteSize !== buffer.byteLength
    ) {
      throw new Error("Master Base Mesh integrity check failed.");
    }
    const digest = await crypto.subtle.digest("SHA-256", buffer);
    const checksum = [...new Uint8Array(digest)]
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("");
    if (metadata.checksum !== `sha256:${checksum}`) {
      throw new Error("Master Base Mesh checksum mismatch.");
    }
  }

  private async renderRoute(route: RouteId): Promise<void> {
    if (route !== "match") this.cleanupMatch();

    if (!this.session?.onboardingComplete && route !== "onboarding") {
      this.router.navigate("onboarding", true);
      return;
    }

    switch (route) {
      case "onboarding":
        this.renderOnboarding();
        break;
      case "home":
        this.renderHome();
        break;
      case "play":
        this.renderPlay();
        break;
      case "setup":
        this.renderSetup();
        break;
      case "club":
        this.renderClub();
        break;
      case "identity":
        this.renderClubIdentity();
        break;
      case "players":
        this.renderPlayers();
        break;
      case "player":
        this.renderPlayerDetail();
        break;
      case "tactics":
        this.renderTactics();
        break;
      case "activities":
        this.renderActivities();
        break;
      case "profile":
        this.renderProfile();
        break;
      case "more":
        this.renderMore();
        break;
      case "settings":
        this.renderSettings();
        break;
      case "match":
        await this.renderMatch();
        break;
      case "results":
        this.renderResults();
        break;
    }
  }

  private renderBoot(fraction: number, label: string): void {
    const progress = Math.round(fraction * 100);
    this.root.innerHTML = `
      <main class="boot-screen">
        <div class="brand-lockup brand-lockup--large" aria-label="KipsBall by Kips Technology">
          <img src="/icons/kipsball-mark.svg" alt="" width="82" height="82" />
          <div>
            <strong>KipsBall</strong>
            <span>Kips Technology</span>
          </div>
        </div>
        <div class="boot-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progress}">
          <i style="width:${progress}%"></i>
        </div>
        <p>${escapeHtml(label)}</p>
      </main>
    `;
  }

  private renderBlockingError(title: string, detail: string, retry: () => void): void {
    this.root.innerHTML = `
      <main class="center-screen">
        <section class="error-panel">
          <span class="eyebrow">STARTUP ERROR · KB-${Date.now().toString().slice(-6)}</span>
          <h1>${escapeHtml(title)}</h1>
          <p>${escapeHtml(detail)}</p>
          <button class="button button--primary" id="retry-boot">Retry</button>
        </section>
      </main>
    `;
    this.root.querySelector("#retry-boot")?.addEventListener("click", retry);
  }

  private renderOnboarding(): void {
    const locale = deviceLocale();
    this.root.innerHTML = `
      <main class="onboarding screen-scroll">
        <section class="onboarding-hero">
          <div class="brand-lockup brand-lockup--large">
            <img src="/icons/kipsball-mark.svg" alt="" width="76" height="76" />
            <div><strong>KipsBall</strong><span>Kips Technology</span></div>
          </div>
          <div class="onboarding-copy">
            <span class="eyebrow">ORIGINAL REAL-TIME 3D FOOTBALL</span>
            <h1>Your club.<br /><em>Your matchday.</em></h1>
          </div>
          <p>
            Create a local identity, prepare your eleven, then step into one connected
            full-screen 3D football journey.
          </p>
          <div class="onboarding-proof" aria-label="Current product capabilities">
            <span>${uiIcon("stadium")} Full 3D stadium</span>
            <span>${uiIcon("tactics")} 11 v 11 simulation</span>
            <span>${uiIcon("shield")} Local persistence</span>
          </div>
          <div class="onboarding-orbit" aria-hidden="true">
            <i></i><i></i><i></i>
            <div class="onboarding-ball">${uiIcon("ball")}</div>
          </div>
        </section>
        <section class="onboarding-card">
          <div class="form-heading">
            <span>01 / CLUB IDENTITY</span>
            <h2>Start your local club</h2>
            <p>Progress stays on this device. You can edit the club later.</p>
          </div>
          <label>
            <span>Display name</span>
            <input id="display-name" maxlength="24" value="KipsPlayer" autocomplete="nickname" />
          </label>
          <label>
            <span>Club name</span>
            <input id="club-name" maxlength="28" value="Kips United" />
          </label>
          <label>
            <span>Language</span>
            <select id="locale">
              <option value="en" ${locale === "en" ? "selected" : ""}>English</option>
              <option value="id" ${locale === "id" ? "selected" : ""}>Bahasa Indonesia</option>
            </select>
          </label>
          <button class="button button--primary button--wide" id="create-guest">
            <span>Enter KipsBall</span>${uiIcon("arrow")}
          </button>
          <div class="local-note">${uiIcon("check")} No password, payment, or fake online opponent.</div>
        </section>
      </main>
    `;

    this.root.querySelector("#create-guest")?.addEventListener("click", () => {
      const displayName =
        (this.root.querySelector("#display-name") as HTMLInputElement | null)?.value.trim() ||
        "KipsPlayer";
      const clubName =
        (this.root.querySelector("#club-name") as HTMLInputElement | null)?.value.trim() ||
        "Kips United";
      const selectedLocale =
        ((this.root.querySelector("#locale") as HTMLSelectElement | null)?.value as "en" | "id") ||
        "en";
      this.session = {
        schemaVersion: 1,
        guestId: crypto.randomUUID(),
        displayName: displayName.slice(0, 24),
        locale: selectedLocale,
        xp: 0,
        club: {
          ...DEFAULT_CLUB,
          name: clubName.slice(0, 28),
          shortName: shortName(clubName)
        },
        onboardingComplete: true
      };
      this.persistence.saveSession(this.session);
      this.clubPlan = createDefaultClubPlan();
      this.persistence.saveClubPlan(this.clubPlan);
      this.router.navigate("home");
    });
  }

  private renderHome(): void {
    if (!this.session || !this.capabilities) return;
    const latest = this.history[0];
    const level = Math.floor(this.session.xp / 500) + 1;
    const levelProgress = Math.round(((this.session.xp % 500) / 500) * 100);
    const matchRoute = this.activeMatch ? "match" : "setup";
    const matchAction = this.activeMatch ? "Continue match" : "Set up match";
    const matchStatus = this.activeMatch
      ? `Saved at ${formatClock(this.activeMatch.clockSeconds)}`
      : "Ready to configure";
    const matchScore = this.activeMatch
      ? `${this.activeMatch.homeScore} – ${this.activeMatch.awayScore}`
      : "VS";
    const opponentShort = this.activeMatch?.config.awayShortName ?? "AUR";
    const opponentName = this.activeMatch?.config.awayName ?? "Aurora Athletic";

    this.renderShell(
      "home",
      `
        <section class="home-heading home-heading--premium">
          <div>
            <span class="eyebrow">KIPSBALL COMMAND CENTRE</span>
            <h1>Ready, ${escapeHtml(this.session.displayName)}?</h1>
            <p>Your next useful action is always first.</p>
          </div>
          <div class="level-block">
            <div><span>LOCAL LEVEL</span><b>${level}</b></div>
            <i><span style="width:${levelProgress}%"></span></i>
            <small>${this.session.xp % 500} / 500 XP</small>
          </div>
        </section>

        <section class="matchday-hero">
          <div class="matchday-copy">
            <div class="matchday-label"><span>01</span><b>LOCAL MATCHDAY</b><i>OFFLINE</i></div>
            <h2>${this.activeMatch ? "The match is waiting." : "Walk into matchday."}</h2>
            <p>
              ${this.activeMatch
                ? "Resume the exact saved simulation state—score, clock, players and ball."
                : "Choose your opponent and difficulty, review the matchup, then enter the same full-screen 3D match state."}
            </p>
            <div class="matchday-meta">
              <span>${uiIcon("stadium")} Kips Stadium</span>
              <span>${uiIcon("clock")} 4–6 min</span>
              <span>${uiIcon("tactics")} ${FORMATIONS[this.clubPlan.formationId].id}</span>
            </div>
            <button class="button button--primary button--hero" data-route="${matchRoute}">
              <span>${matchAction}</span>${uiIcon("arrow")}
            </button>
          </div>
          <div class="matchday-visual" aria-label="${escapeHtml(this.session.club.name)} versus ${escapeHtml(opponentName)}">
            <div class="stadium-lights"><i></i><i></i><i></i><i></i></div>
            <div class="matchup-lockup">
              <div class="mini-crest" style="--club:${escapeHtml(this.session.club.primaryColor)}">
                <span>${escapeHtml(this.session.club.shortName)}</span>
              </div>
              <div class="matchup-score">
                <small>${matchStatus}</small>
                <b>${matchScore}</b>
                <span>11 v 11</span>
              </div>
              <div class="mini-crest mini-crest--away">
                <span>${escapeHtml(opponentShort)}</span>
              </div>
            </div>
            <div class="hero-pitch"><i></i><span></span></div>
          </div>
        </section>

        <section class="section-heading">
          <div><span class="eyebrow">CLUB OVERVIEW</span><h2>Everything match-ready</h2></div>
          <button class="text-link" data-route="club">Open My Club ${uiIcon("arrow")}</button>
        </section>
        <section class="home-dashboard">
          <article class="dashboard-card dashboard-card--club">
            <div class="card-icon">${uiIcon("shield")}</div>
            <div>
              <span class="card-label">MY CLUB</span>
              <h3>${escapeHtml(this.session.club.name)}</h3>
              <p><b>Starting XI ready</b> · ${FORMATIONS[this.clubPlan.formationId].name}</p>
            </div>
            <div class="kit-swatch" aria-label="Club colors">
              <i style="--swatch:${escapeHtml(this.session.club.primaryColor)}"></i>
              <i style="--swatch:${escapeHtml(this.session.club.secondaryColor)}"></i>
            </div>
          </article>

          <article class="dashboard-card">
            <div class="card-icon">${uiIcon("history")}</div>
            <div>
              <span class="card-label">RECENT MATCH</span>
              ${
                latest
                  ? `<h3>${latest.homeScore}–${latest.awayScore}</h3><p>${escapeHtml(latest.homeName)} · ${escapeHtml(latest.awayName)}</p>`
                  : `<h3>No result yet</h3><p>Finish a match to build local history.</p>`
              }
            </div>
            <button class="icon-link" data-route="play" aria-label="Open Play Hub">${uiIcon("arrow")}</button>
          </article>

          <article class="dashboard-card">
            <div class="card-icon">${uiIcon("device")}</div>
            <div>
              <span class="card-label">DEVICE PROFILE</span>
              <h3>${this.capabilities.recommendedPreset.toUpperCase()}</h3>
              <p>WebGL 2 · ${this.capabilities.logicalCores} logical cores</p>
            </div>
            <span class="status-pill status-pill--ready">${uiIcon("check")} READY</span>
          </article>

          <article class="dashboard-card dashboard-card--service">
            <div class="card-icon">${uiIcon("spark")}</div>
            <div>
              <span class="card-label">LIVE SERVICES</span>
              <h3>Local core only</h3>
              <p>Online modes stay hidden until server authority is real.</p>
            </div>
            <span class="status-pill">TRUTHFUL</span>
          </article>
        </section>
      `
    );
  }

  private renderPlay(): void {
    const recentRows = this.history
      .slice(0, 4)
      .map(
        (match) => `
          <div class="history-row">
            <span class="history-result history-result--${match.result}">${match.result[0]!.toUpperCase()}</span>
            <div><b>${escapeHtml(match.homeName)}</b><small>${escapeHtml(match.awayName)}</small></div>
            <strong>${match.homeScore} – ${match.awayScore}</strong>
          </div>`
      )
      .join("");

    this.renderShell(
      "play",
      `
        <section class="page-heading page-heading--split">
          <div>
            <span class="eyebrow">PLAY / MATCH CENTRE</span>
            <h1>Choose how you play.</h1>
            <p>Playable modes are separated from service-dependent modes, with no fake opponents or dead primary actions.</p>
          </div>
          <div class="play-readiness">
            <span>${uiIcon("check")} CORE READY</span>
            <b>1 playable mode</b>
          </div>
        </section>

        <section class="play-stage">
          <article class="play-feature">
            <div class="play-feature__visual" aria-hidden="true">
              <div class="mode-number">01</div>
              <div class="tactical-orbit">
                <i></i><i></i><i></i>
                <span>${uiIcon("ball")}</span>
              </div>
              <div class="mode-score"><b>11</b><i>v</i><b>11</b></div>
              <div class="mode-horizon"></div>
            </div>
            <div class="play-feature__copy">
              <div class="mode-title-row">
                <div><span class="eyebrow">OFFLINE · LOCAL AI</span><h2>Local Exhibition</h2></div>
                <span class="status-pill status-pill--ready">${uiIcon("check")} PLAYABLE</span>
              </div>
              <p>Complete the connected journey from team review to broadcast presentation, live 3D match, half-time, full-time and persistent result.</p>
              <div class="mode-specs">
                <div><span>TEAM TYPE</span><b>Your club</b></div>
                <div><span>DURATION</span><b>4 or 6 min</b></div>
                <div><span>HOST</span><b>Local authority</b></div>
                <div><span>INPUT</span><b>Touch + keys</b></div>
              </div>
              <button class="button button--primary button--hero" data-route="setup">
                <span>Configure match</span>${uiIcon("arrow")}
              </button>
            </div>
          </article>
        </section>

        <section class="play-lower-grid">
          <article class="service-panel">
            <div class="section-heading section-heading--compact">
              <div><span class="eyebrow">SERVICE STATUS</span><h2>Protected until real</h2></div>
            </div>
            <div class="service-list">
              <div class="service-item" aria-disabled="true">
                <span class="service-index">02</span>
                <div><b>Kips Ranked</b><small>Needs regional authoritative match hosts.</small></div>
                <em>BLOCKED</em>
              </div>
              <div class="service-item" aria-disabled="true">
                <span class="service-index">03</span>
                <div><b>Quick Room</b><small>Needs verified rooms, invites and result authority.</small></div>
                <em>PLANNED</em>
              </div>
              <div class="service-item" aria-disabled="true">
                <span class="service-index">04</span>
                <div><b>Kips Tournament</b><small>Needs draw, schedule and save/resume loop.</small></div>
                <em>PLANNED</em>
              </div>
            </div>
          </article>

          <article class="history-panel">
            <div class="section-heading section-heading--compact">
              <div><span class="eyebrow">LOCAL HISTORY</span><h2>Recent matches</h2></div>
              <span>${this.history.length} saved</span>
            </div>
            <div class="history-list">
              ${recentRows || `<div class="empty-state">${uiIcon("history")}<b>No match history</b><span>Your first full-time result appears here.</span></div>`}
            </div>
          </article>
        </section>
      `
    );
  }

  private renderSetup(): void {
    if (!this.session) return;
    const selectedOpponent = OPPONENTS[this.pendingMatch.opponent];
    const formation = FORMATIONS[this.clubPlan.formationId];
    const tactic = TACTICAL_PRESETS[this.clubPlan.tacticalPreset];
    const captain = playerById(this.clubPlan.captainId);
    this.renderShell(
      "play",
      `
        <section class="page-heading setup-heading">
          <button class="text-back" data-route="play">${uiIcon("arrow")} Play Hub</button>
          <span class="eyebrow">LOCAL EXHIBITION / MATCHDAY</span>
          <h1>Prepare the fixture.</h1>
          <p>Review the teams, avoid a kit clash, set the rules, then enter the 3D presentation.</p>
        </section>

        <ol class="setup-steps" aria-label="Match setup progress">
          <li class="is-complete"><span>${uiIcon("check")}</span><b>YOUR CLUB</b><small>${formation.id} · ${teamRating(this.clubPlan)} OVR</small></li>
          <li class="is-active"><span>02</span><b>MATCH SETTINGS</b><small>Opponent and rules</small></li>
          <li><span>03</span><b>3D MATCHDAY</b><small>Presentation and kickoff</small></li>
        </ol>

        <section class="setup-layout">
          <div class="matchup-stage">
            <div class="matchup-stage__top">
              <span>KIPS STADIUM</span>
              <b>LOCAL EXHIBITION</b>
              <span>CLEAR DAY</span>
            </div>
            <div class="matchup-teams">
              <article class="matchup-team">
                <div class="team-crest" style="--club:${escapeHtml(this.session.club.primaryColor)}">
                  ${escapeHtml(this.session.club.shortName)}
                </div>
                <span>HOME</span>
                <h2>${escapeHtml(this.session.club.name)}</h2>
                <small>${formation.name} · ${tactic.name}</small>
              </article>
              <div class="versus-mark"><small>MATCHDAY</small><b>VS</b><span>READY</span></div>
              <article class="matchup-team">
                <div class="team-crest team-crest--away" id="opponent-crest" style="--club:${escapeHtml(selectedOpponent.color)}">
                  ${escapeHtml(selectedOpponent.shortName)}
                </div>
                <span>AWAY</span>
                <h2 id="opponent-name">${escapeHtml(selectedOpponent.name)}</h2>
                <small>Adaptive AI</small>
              </article>
            </div>
            <div class="matchup-pitch" aria-hidden="true"><i></i><span></span></div>
            <div class="kit-review">
              <div>
                <span>HOME KIT</span>
                <i class="kit-shirt" style="--kit:${escapeHtml(this.session.club.primaryColor)};--trim:${escapeHtml(this.session.club.secondaryColor)}"></i>
              </div>
              <p>${uiIcon("check")} High-contrast kit pairing</p>
              <div>
                <span>AWAY KIT</span>
                <i class="kit-shirt kit-shirt--away" id="away-kit" style="--kit:${escapeHtml(selectedOpponent.color)};--trim:#f5f7f1"></i>
              </div>
            </div>
          </div>

          <form class="setup-form" id="setup-form">
            <div class="form-heading">
              <span>02 / MATCH SETTINGS</span>
              <h2>Fixture controls</h2>
              <p>Every setting below is connected to the local match state.</p>
            </div>
            <label>
              <span>Opponent</span>
              <select id="opponent">
                <option value="aurora" ${this.pendingMatch.opponent === "aurora" ? "selected" : ""}>Aurora Athletic</option>
                <option value="harbor" ${this.pendingMatch.opponent === "harbor" ? "selected" : ""}>Harbor City</option>
                <option value="mountain" ${this.pendingMatch.opponent === "mountain" ? "selected" : ""}>Mountain Eleven</option>
              </select>
            </label>
            <label>
              <span>Difficulty</span>
              <select id="difficulty">
                <option value="beginner" ${this.pendingMatch.difficulty === "beginner" ? "selected" : ""}>Beginner</option>
                <option value="amateur" ${this.pendingMatch.difficulty === "amateur" ? "selected" : ""}>Amateur</option>
                <option value="professional" ${this.pendingMatch.difficulty === "professional" ? "selected" : ""}>Professional</option>
              </select>
            </label>
            <label>
              <span>Real match duration</span>
              <select id="duration">
                <option value="240" ${this.pendingMatch.realDurationSeconds === 240 ? "selected" : ""}>4 minutes</option>
                <option value="360" ${this.pendingMatch.realDurationSeconds === 360 ? "selected" : ""}>6 minutes</option>
              </select>
            </label>
            <div class="setup-summary">
              <div>${uiIcon("stadium")}<span><small>VENUE</small><b>Kips Stadium</b></span></div>
              <div>${uiIcon("device")}<span><small>HOST</small><b>This device</b></span></div>
              <div>${uiIcon("profile")}<span><small>CAPTAIN</small><b>${escapeHtml(captain?.shortName ?? "Unassigned")}</b></span></div>
              <div>${uiIcon("shield")}<span><small>RESULT</small><b>Saved locally</b></span></div>
            </div>
            <button class="button button--primary button--wide" type="submit">
              <span>Enter 3D matchday</span>${uiIcon("arrow")}
            </button>
            <p class="fine-print fine-print--center">No fake matchmaking, rank, reward currency, or remote opponent.</p>
          </form>
        </section>
      `
    );

    const opponentSelect = this.root.querySelector("#opponent") as HTMLSelectElement | null;
    opponentSelect?.addEventListener("change", () => {
      const definition = OPPONENTS[opponentSelect.value as PendingMatchOptions["opponent"]];
      const crest = this.root.querySelector("#opponent-crest");
      if (crest && definition) {
        crest.textContent = definition.shortName;
        (crest as HTMLElement).style.setProperty("--club", definition.color);
        const opponentName = this.root.querySelector("#opponent-name");
        if (opponentName) opponentName.textContent = definition.name;
        const awayKit = this.root.querySelector("#away-kit") as HTMLElement | null;
        awayKit?.style.setProperty("--kit", definition.color);
      }
    });

    this.root.querySelector("#setup-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const difficulty = (this.root.querySelector("#difficulty") as HTMLSelectElement).value;
      const duration = Number((this.root.querySelector("#duration") as HTMLSelectElement).value);
      this.pendingMatch = {
        opponent: (opponentSelect?.value ?? "aurora") as PendingMatchOptions["opponent"],
        difficulty: difficulty as MatchConfig["difficulty"],
        realDurationSeconds: duration
      };
      void this.createMatchAndEnter();
    });
  }

  private async createMatchAndEnter(): Promise<void> {
    if (!this.session) return;
    const opponent = OPPONENTS[this.pendingMatch.opponent];
    const config: MatchConfig = {
      id: crypto.randomUUID(),
      homeName: this.session.club.name,
      homeShortName: this.session.club.shortName,
      awayName: opponent.name,
      awayShortName: opponent.shortName,
      homeColor: this.session.club.primaryColor,
      awayColor: opponent.color,
      difficulty: this.pendingMatch.difficulty,
      realDurationSeconds: this.pendingMatch.realDurationSeconds,
      createdAt: new Date().toISOString(),
      homeFormation: this.clubPlan.formationId,
      awayFormation: "4-3-3",
      homeTacticalPreset: this.clubPlan.tacticalPreset,
      homeLineup: lineupPlayers(this.clubPlan).map((player) => ({
        profileId: player.id,
        displayName: player.shortName,
        number: player.number
      })),
      homeBench: benchPlayers(this.clubPlan).map((player) => ({
        profileId: player.id,
        displayName: player.shortName,
        number: player.number
      }))
    };
    this.simulation = new MatchSimulation(config);
    this.activeMatch = this.simulation.snapshot();
    await this.persistence.saveActiveMatch(this.activeMatch);

    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen is optional and may be denied by the browser.
    }
    try {
      await screen.orientation.lock("landscape");
    } catch {
      // The match provides an orientation interstitial when locking is unavailable.
    }
    this.router.navigate("match");
  }

  private renderClub(): void {
    if (!this.session) return;
    const formation = FORMATIONS[this.clubPlan.formationId];
    const tactic = TACTICAL_PRESETS[this.clubPlan.tacticalPreset];
    const starters = lineupPlayers(this.clubPlan);
    const reserves = benchPlayers(this.clubPlan);
    const captain = playerById(this.clubPlan.captainId);
    const players = formation.slots
      .map((slot, index) => {
        const player = starters[index];
        if (!player) return "";
        return `
          <button
            class="lineup-player"
            type="button"
            data-player="${player.id}"
            style="--slot-x:${slot.x}%;--slot-y:${slot.y}%"
            aria-label="${escapeHtml(player.name)}, number ${player.number}, ${slot.label}"
          >
            <span>${slot.label}</span>
            <i style="--player-color:${escapeHtml(this.session!.club.primaryColor)}">${player.number}</i>
            <b>${escapeHtml(player.shortName)}</b>
            <small>${player.rating}</small>
            ${this.clubPlan.captainId === player.id ? `<em>C</em>` : ""}
          </button>`;
      })
      .join("");
    const bench = reserves
      .map(
        (player) => `
          <button class="squad-strip__player" type="button" data-player="${player.id}">
            <i style="--player-color:${escapeHtml(this.session!.club.primaryColor)}">${player.number}</i>
            <span><b>${escapeHtml(player.shortName)}</b><small>${player.position} · ${player.rating}</small></span>
          </button>`
      )
      .join("");

    this.renderShell(
      "club",
      `
        <section class="page-heading page-heading--split club-heading">
          <div>
            <span class="eyebrow">CLUB / SQUAD</span>
            <h1>${escapeHtml(this.session.club.name)}</h1>
            <p>Your selected eleven, captain, formation and tactical profile feed the next local match.</p>
          </div>
          <div class="club-readiness">
            <span>${uiIcon("check")} MATCH READY</span>
            <div><b>${teamRating(this.clubPlan)}</b><small>Team rating</small></div>
          </div>
        </section>
        ${this.clubTabs("club")}
        <section class="club-layout">
          <article class="club-editor club-command-card">
            <div class="club-identity-preview">
              <div class="mini-crest" style="--club:${escapeHtml(this.session.club.primaryColor)}">
                <span>${escapeHtml(this.session.club.shortName)}</span>
              </div>
              <div><span>MY TEAM</span><h2>${escapeHtml(this.session.club.name)}</h2><p>18 original players · local save</p></div>
            </div>
            <div class="club-stat-list">
              <div>${uiIcon("tactics")}<span><small>FORMATION</small><b>${formation.name}</b></span></div>
              <div>${uiIcon("spark")}<span><small>TACTICAL IDEA</small><b>${tactic.name}</b></span></div>
              <div>${uiIcon("profile")}<span><small>CAPTAIN</small><b>${escapeHtml(captain?.name ?? "Not assigned")}</b></span></div>
            </div>
            <button class="button button--primary button--wide" type="button" data-route="players">
              <span>Open player inventory</span>${uiIcon("arrow")}
            </button>
            <button class="button button--quiet button--wide" type="button" id="auto-build">
              Auto Build best fit
            </button>
            <p class="fine-print">Auto Build evaluates rating and positional fit, then saves a reversible local selection.</p>
          </article>
          <div class="lineup-panel">
            <header class="lineup-panel__header">
              <div><span>STARTING XI</span><h2>${formation.name}</h2></div>
              <div class="team-rating"><span>TEAM</span><b>${teamRating(this.clubPlan)}</b></div>
            </header>
            <div class="lineup-field" aria-label="Starting eleven in ${formation.name}" style="--club-color:${escapeHtml(this.session.club.primaryColor)}">
              ${players}
            </div>
            <div class="lineup-legend">
              <span><i></i> Natural or compatible role</span>
              <span>${uiIcon("tactics")} Match anchors connected</span>
            </div>
          </div>
        </section>
        <section class="squad-strip">
          <div class="squad-strip__heading"><span>BENCH & RESERVES</span><b>${reserves.length} available</b></div>
          ${bench}
          <button class="squad-strip__lock" data-route="players"><span>FULL INVENTORY</span><b>Search, filter and inspect all 18</b></button>
        </section>
      `
    );

    this.bindPlayerLinks();
    this.root.querySelector("#auto-build")?.addEventListener("click", () => {
      const startingIds = autoBuildLineup(this.clubPlan.formationId);
      this.clubPlan = {
        ...this.clubPlan,
        startingIds,
        captainId: startingIds.includes(this.clubPlan.captainId)
          ? this.clubPlan.captainId
          : startingIds[1]!
      };
      this.persistence.saveClubPlan(this.clubPlan);
      this.showToast("Best-fit eleven saved");
      window.setTimeout(() => this.renderClub(), 300);
    });
  }

  private renderClubIdentity(): void {
    if (!this.session) return;
    const identityFormation = FORMATIONS[this.clubPlan.formationId];
    const players = PLAYER_NAMES.map(
      ([number, name, role], index) => `
        <button class="lineup-player" type="button" data-player="${this.clubPlan.startingIds[index] ?? ""}" style="--slot-x:${identityFormation.slots[index]?.x ?? 50}%;--slot-y:${identityFormation.slots[index]?.y ?? 50}%" aria-label="${name}, number ${number}, ${role}">
          <span>${role}</span>
          <i style="--player-color:${escapeHtml(this.session!.club.primaryColor)}">${number}</i>
          <b>${name}</b>
          <small>${index === 0 ? 76 : 72 + ((index * 3) % 9)}</small>
        </button>`
    ).join("");

    const bench = PLAYER_NAMES.slice(5, 10)
      .map(
        ([number, name, role], index) => `
          <div class="squad-strip__player">
            <i style="--player-color:${escapeHtml(this.session!.club.primaryColor)}">${number}</i>
            <span><b>${name}</b><small>${role} · ${74 + index}</small></span>
          </div>`
      )
      .join("");

    this.renderShell(
      "club",
      `
        <section class="page-heading page-heading--split club-heading">
          <div>
            <span class="eyebrow">CLUB / IDENTITY</span>
            <h1>Build a recognizable club.</h1>
            <p>Edit the original club name, crest monogram and kit colors used across matchday.</p>
          </div>
          <div class="club-readiness">
            <span>${uiIcon("check")} ORIGINAL IDENTITY</span>
            <div><b>${escapeHtml(this.session.club.shortName)}</b><small>Club code</small></div>
          </div>
        </section>

        ${this.clubTabs("identity")}

        <section class="club-layout">
          <form class="club-editor" id="club-form">
            <div class="club-identity-preview">
              <div class="mini-crest" id="club-preview-crest" style="--club:${escapeHtml(this.session.club.primaryColor)}">
                <span>${escapeHtml(this.session.club.shortName)}</span>
              </div>
              <div><span>MY CLUB</span><h2>${escapeHtml(this.session.club.name)}</h2><p>Guest · Local save</p></div>
            </div>
            <label>
              <span>Club name</span>
              <input id="edit-club-name" maxlength="28" value="${escapeHtml(this.session.club.name)}" />
            </label>
            <div class="color-row">
              <label><span>Primary kit</span><input type="color" id="primary-color" value="${escapeHtml(this.session.club.primaryColor)}" /></label>
              <label><span>Secondary kit</span><input type="color" id="secondary-color" value="${escapeHtml(this.session.club.secondaryColor)}" /></label>
            </div>
            <div class="club-stat-list">
              <div>${uiIcon("tactics")}<span><small>FORMATION</small><b>${FORMATIONS[this.clubPlan.formationId].name}</b></span></div>
              <div>${uiIcon("stadium")}<span><small>HOME GROUND</small><b>Kips Stadium</b></span></div>
              <div>${uiIcon("shield")}<span><small>STATE</small><b>Saved on device</b></span></div>
            </div>
            <button class="button button--primary button--wide" type="submit">
              <span>Save club identity</span>${uiIcon("check")}
            </button>
          </form>

          <div class="lineup-panel">
            <header class="lineup-panel__header">
              <div><span>STARTING XI</span><h2>4-3-3 Balanced</h2></div>
              <div class="team-rating"><span>TEAM</span><b>76</b></div>
            </header>
            <div class="lineup-field" aria-label="Starting eleven in ${identityFormation.name}" style="--club-color:${escapeHtml(this.session.club.primaryColor)}">
              ${players}
            </div>
            <div class="lineup-legend">
              <span><i></i> Natural position</span>
              <span>${uiIcon("tactics")} Simulation anchors connected</span>
            </div>
          </div>
        </section>

        <section class="squad-strip">
          <div class="squad-strip__heading"><span>LOCAL SQUAD</span><b>Matchday options</b></div>
          ${bench}
          <div class="squad-strip__lock"><span>BENCH EDITOR</span><b>Not exposed until substitutions are functional</b></div>
        </section>
      `
    );

    this.bindPlayerLinks();
    const updateClubPreview = (): void => {
      const primary = (this.root.querySelector("#primary-color") as HTMLInputElement | null)?.value;
      const name = (this.root.querySelector("#edit-club-name") as HTMLInputElement | null)?.value;
      const crest = this.root.querySelector("#club-preview-crest") as HTMLElement | null;
      if (primary) crest?.style.setProperty("--club", primary);
      const initials = crest?.querySelector("span");
      if (initials && name) initials.textContent = shortName(name);
    };
    this.root.querySelector("#primary-color")?.addEventListener("input", updateClubPreview);
    this.root.querySelector("#edit-club-name")?.addEventListener("input", updateClubPreview);

    this.root.querySelector("#club-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!this.session) return;
      const name =
        (this.root.querySelector("#edit-club-name") as HTMLInputElement).value.trim() ||
        "Kips United";
      this.session.club = {
        ...this.session.club,
        name: name.slice(0, 28),
        shortName: shortName(name),
        primaryColor: (this.root.querySelector("#primary-color") as HTMLInputElement).value,
        secondaryColor: (this.root.querySelector("#secondary-color") as HTMLInputElement).value,
        formation: this.clubPlan.formationId
      };
      this.persistence.saveSession(this.session);
      this.showToast("Club saved");
      window.setTimeout(() => this.renderClubIdentity(), 420);
    });
  }

  private renderPlayers(): void {
    if (!this.session) return;
    const starting = new Set(this.clubPlan.startingIds);
    const normalizedSearch = this.playerSearch.trim().toLocaleLowerCase();
    const visiblePlayers = SQUAD_PLAYERS.filter((player) => {
      const matchesSearch =
        !normalizedSearch ||
        player.name.toLocaleLowerCase().includes(normalizedSearch) ||
        player.position.toLocaleLowerCase().includes(normalizedSearch);
      const matchesFilter =
        this.playerFilter === "ALL" ||
        (this.playerFilter === "XI" && starting.has(player.id)) ||
        player.position === this.playerFilter;
      return matchesSearch && matchesFilter;
    }).sort(
      (a, b) =>
        Number(starting.has(b.id)) - Number(starting.has(a.id)) || b.rating - a.rating
    );
    const cards = visiblePlayers
      .map(
        (player) => `
          <button class="inventory-player-card ${this.clubPlan.selectedPlayerId === player.id ? "is-selected" : ""}" type="button" data-player="${player.id}">
            <div class="inventory-rating"><b>${player.rating}</b><span>${player.position}</span></div>
            <div class="player-silhouette" style="--player-color:${escapeHtml(this.session!.club.primaryColor)}">
              <i></i><span>${player.number}</span>
            </div>
            <div class="inventory-player-copy">
              <small>${starting.has(player.id) ? "STARTING XI" : "SQUAD"}</small>
              <strong>${escapeHtml(player.name)}</strong>
              <span>${player.preferredFoot} foot · ${escapeHtml(player.ability)}</span>
            </div>
            ${this.clubPlan.captainId === player.id ? `<em>CAPTAIN</em>` : ""}
          </button>`
      )
      .join("");

    this.renderShell(
      "club",
      `
        <section class="page-heading page-heading--split club-heading">
          <div>
            <span class="eyebrow">CLUB / PLAYER INVENTORY</span>
            <h1>Know every option.</h1>
            <p>Search, filter and return to the exact selected player without losing context.</p>
          </div>
          <div class="inventory-count"><b>${visiblePlayers.length}</b><span>of ${SQUAD_PLAYERS.length} players</span></div>
        </section>
        ${this.clubTabs("players")}
        <section class="inventory-toolbar">
          <label>
            <span>Search player or position</span>
            <input id="player-search" value="${escapeHtml(this.playerSearch)}" placeholder="Name or position" autocomplete="off" />
          </label>
          <label>
            <span>Filter</span>
            <select id="player-filter">
              ${["ALL", "XI", "GK", "LB", "CB", "RB", "CM", "LW", "RW", "ST"]
                .map(
                  (value) =>
                    `<option value="${value}" ${this.playerFilter === value ? "selected" : ""}>${value === "ALL" ? "All players" : value === "XI" ? "Starting XI" : value}</option>`
                )
                .join("")}
            </select>
          </label>
          <div class="inventory-toolbar__summary"><span>TEAM RATING</span><b>${teamRating(this.clubPlan)}</b><small>${FORMATIONS[this.clubPlan.formationId].name}</small></div>
        </section>
        <section class="player-inventory-grid">
          ${cards || `<div class="empty-state inventory-empty">${uiIcon("players")}<b>No player matches</b><span>Change the search or filter.</span></div>`}
        </section>
      `
    );

    this.bindPlayerLinks();
    const search = this.root.querySelector("#player-search") as HTMLInputElement | null;
    search?.addEventListener("input", () => {
      this.playerSearch = search.value;
      this.renderPlayers();
      const restored = this.root.querySelector("#player-search") as HTMLInputElement | null;
      restored?.focus();
      restored?.setSelectionRange(restored.value.length, restored.value.length);
    });
    this.root.querySelector("#player-filter")?.addEventListener("change", (event) => {
      this.playerFilter = (event.currentTarget as HTMLSelectElement).value;
      this.renderPlayers();
    });
  }

  private renderPlayerDetail(): void {
    if (!this.session) return;
    const player = playerById(this.clubPlan.selectedPlayerId) ?? SQUAD_PLAYERS[0]!;
    const starter = this.clubPlan.startingIds.includes(player.id);
    const captain = this.clubPlan.captainId === player.id;
    const swapTargets = compatibleSwapTargets(this.clubPlan, player.id);
    const attributeRows = Object.entries(player.attributes)
      .map(
        ([name, value]) => `
          <div class="attribute-row">
            <span>${name.toUpperCase()}</span><i><b style="width:${value}%"></b></i><strong>${value}</strong>
          </div>`
      )
      .join("");

    this.renderShell(
      "club",
      `
        <section class="player-detail-heading">
          <button class="text-back" data-route="players">${uiIcon("arrow")} Player inventory</button>
          <span>${starter ? "STARTING XI" : "SQUAD OPTION"} · #${player.number}</span>
        </section>
        ${this.clubTabs("players")}
        <section class="player-detail-layout">
          <article class="player-hero-card">
            <div class="player-hero-rating"><b>${player.rating}</b><span>${player.position}</span></div>
            <div class="player-hero-figure" style="--player-color:${escapeHtml(this.session.club.primaryColor)}">
              <i></i><span>${player.number}</span>
            </div>
            <div class="player-hero-copy">
              <small>ORIGINAL KIPSBALL PLAYER</small>
              <h1>${escapeHtml(player.name)}</h1>
              <p>${player.preferredFoot} foot · ${player.heightCm} cm · ${player.alternatePositions.length ? `Also ${player.alternatePositions.join(", ")}` : "Natural role only"}</p>
              <div class="ability-chip">${uiIcon("spark")}<span><b>${escapeHtml(player.ability)}</b><small>${escapeHtml(player.abilityDetail)}</small></span></div>
            </div>
          </article>
          <article class="player-attributes-card">
            <header><span class="eyebrow">BASE PROFILE</span><h2>Attributes</h2></header>
            <div class="attribute-list">${attributeRows}</div>
          </article>
          <aside class="player-actions-card">
            <span class="eyebrow">SQUAD ACTIONS</span>
            <h2>${starter ? "Matchday role" : "Move into the XI"}</h2>
            ${
              starter
                ? `<div class="starter-state">${uiIcon("check")} Selected in ${FORMATIONS[this.clubPlan.formationId].name}</div>
                   <button class="button button--primary button--wide" id="assign-captain" ${captain ? "disabled" : ""}>
                     ${captain ? "Current captain" : "Assign captain"}
                   </button>
                   <p class="fine-print">To replace this player, open a compatible reserve and choose the outgoing starter.</p>`
                : swapTargets.length
                  ? `<form id="lineup-swap-form">
                       <label><span>Replace compatible starter</span>
                         <select id="swap-target">
                           ${swapTargets.map((target) => `<option value="${target.id}">${escapeHtml(target.name)} · ${target.position} ${target.rating}</option>`).join("")}
                         </select>
                       </label>
                       <button class="button button--primary button--wide" type="submit">Confirm lineup swap</button>
                     </form>
                     <p class="fine-print">The selected starter moves to reserves. No player is deleted or consumed.</p>`
                  : `<div class="blocked-state">No compatible slot in the current formation.</div>`
            }
            <button class="button button--quiet button--wide" data-route="club">View starting XI</button>
          </aside>
        </section>
      `
    );

    this.root.querySelector("#assign-captain")?.addEventListener("click", () => {
      this.clubPlan = { ...this.clubPlan, captainId: player.id };
      this.persistence.saveClubPlan(this.clubPlan);
      this.renderPlayerDetail();
    });
    this.root.querySelector("#lineup-swap-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const outgoingId = (this.root.querySelector("#swap-target") as HTMLSelectElement).value;
      this.clubPlan = swapIntoLineup(this.clubPlan, player.id, outgoingId);
      this.persistence.saveClubPlan(this.clubPlan);
      this.showToast("Starting XI updated");
      window.setTimeout(() => this.renderPlayerDetail(), 300);
    });
  }

  private renderTactics(): void {
    const activeFormation = FORMATIONS[this.clubPlan.formationId];
    const activeTactic = TACTICAL_PRESETS[this.clubPlan.tacticalPreset];
    const formationCards = Object.values(FORMATIONS)
      .map(
        (formation) => `
          <button class="formation-option ${formation.id === activeFormation.id ? "is-selected" : ""}" type="button" data-formation="${formation.id}">
            <span>${formation.id}</span><b>${formation.name}</b><small>${formation.summary}</small>
          </button>`
      )
      .join("");
    const tacticCards = Object.values(TACTICAL_PRESETS)
      .map(
        (tactic) => `
          <button class="tactic-option ${tactic.id === activeTactic.id ? "is-selected" : ""}" type="button" data-tactic="${tactic.id}">
            <div><span>${tactic.name}</span><b>${tactic.summary}</b></div>
            <i><small>BUILD</small><strong>${tactic.buildUp}</strong></i>
            <i><small>WIDTH</small><strong>${tactic.width}</strong></i>
            <i><small>PRESS</small><strong>${tactic.pressure}</strong></i>
          </button>`
      )
      .join("");

    this.renderShell(
      "club",
      `
        <section class="page-heading page-heading--split club-heading">
          <div>
            <span class="eyebrow">CLUB / TACTICS</span>
            <h1>Shape the next match.</h1>
            <p>Formation anchors and tactical movement values are saved and passed into local simulation.</p>
          </div>
          <span class="status-pill status-pill--ready">${uiIcon("check")} SIMULATION CONNECTED</span>
        </section>
        ${this.clubTabs("tactics")}
        <section class="tactics-workspace">
          <article class="formation-selector">
            <header><span class="eyebrow">FORMATION</span><h2>Base shape</h2></header>
            <div class="formation-options">${formationCards}</div>
          </article>
          <article class="tactic-selector">
            <header><span class="eyebrow">TACTICAL PROFILE</span><h2>Team intent</h2></header>
            <div class="tactic-options">${tacticCards}</div>
          </article>
          <aside class="tactic-connection-card">
            <span class="eyebrow">ACTIVE PLAN</span>
            <h2>${activeFormation.id}<br />${activeTactic.name}</h2>
            <p>${activeTactic.summary}</p>
            <dl>
              <div><dt>Starting XI</dt><dd>11 / 11</dd></div>
              <div><dt>Team rating</dt><dd>${teamRating(this.clubPlan)}</dd></div>
              <div><dt>Next match</dt><dd>Connected</dd></div>
            </dl>
            <button class="button button--primary button--wide" data-route="setup">Review match setup</button>
          </aside>
        </section>
      `
    );

    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-formation]")) {
      button.addEventListener("click", () => {
        const formationId = button.dataset.formation as FormationId;
        const startingIds = autoBuildLineup(formationId);
        this.clubPlan = {
          ...this.clubPlan,
          formationId,
          startingIds,
          captainId: startingIds.includes(this.clubPlan.captainId)
            ? this.clubPlan.captainId
            : startingIds[1]!
        };
        if (this.session) {
          this.session.club.formation = formationId;
          this.persistence.saveSession(this.session);
        }
        this.persistence.saveClubPlan(this.clubPlan);
        this.renderTactics();
      });
    }
    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-tactic]")) {
      button.addEventListener("click", () => {
        this.clubPlan = {
          ...this.clubPlan,
          tacticalPreset: button.dataset.tactic as TacticalPresetId
        };
        this.persistence.saveClubPlan(this.clubPlan);
        this.renderTactics();
      });
    }
  }

  private renderActivities(): void {
    const played = this.history.length;
    const wins = this.history.filter((match) => match.result === "win").length;
    const goals = this.history.reduce((total, match) => total + match.homeScore, 0);
    const milestones = [
      { name: "First whistle", detail: "Finish one local exhibition", value: played, target: 1 },
      { name: "Three match rhythm", detail: "Finish three local exhibitions", value: played, target: 3 },
      { name: "Goal trail", detail: "Score three goals across local matches", value: goals, target: 3 },
      { name: "Winning shape", detail: "Win one local exhibition", value: wins, target: 1 }
    ];
    const milestoneCards = milestones
      .map((item) => {
        const progress = Math.min(100, Math.round((item.value / item.target) * 100));
        return `
          <article class="milestone-card ${progress === 100 ? "is-complete" : ""}">
            <div>${progress === 100 ? uiIcon("check") : uiIcon("activities")}</div>
            <span>${progress === 100 ? "COMPLETE" : "LOCAL MILESTONE"}</span>
            <h3>${item.name}</h3>
            <p>${item.detail}</p>
            <i><b style="width:${progress}%"></b></i>
            <small>${Math.min(item.value, item.target)} / ${item.target}</small>
          </article>`;
      })
      .join("");

    this.renderShell(
      "activities",
      `
        <section class="page-heading page-heading--split">
          <div>
            <span class="eyebrow">ACTIVITIES / LOCAL PROGRESS</span>
            <h1>A useful next target.</h1>
            <p>Progress is derived from saved match results. There are no fake live events or unverified rewards.</p>
          </div>
          <div class="activity-score"><b>${milestones.filter((item) => item.value >= item.target).length}</b><span>of ${milestones.length} milestones</span></div>
        </section>
        <section class="activity-hero">
          <div>
            <span>FEATURED LOCAL ACTIVITY</span>
            <h2>Matchday Fundamentals</h2>
            <p>Review your squad, choose a tactical profile, complete a full 3D match and return to a saved result.</p>
            <button class="button button--primary" data-route="setup">Start matchday ${uiIcon("arrow")}</button>
          </div>
          <div class="activity-ring"><b>${played}</b><span>MATCHES</span><i></i></div>
        </section>
        <section class="section-heading"><div><span class="eyebrow">PROGRESS</span><h2>Local milestones</h2></div><span class="status-pill">NO PURCHASES</span></section>
        <section class="milestone-grid">${milestoneCards}</section>
      `
    );
  }

  private renderProfile(): void {
    if (!this.session) return;
    const played = this.history.length;
    const wins = this.history.filter((match) => match.result === "win").length;
    const draws = this.history.filter((match) => match.result === "draw").length;
    const goals = this.history.reduce((total, match) => total + match.homeScore, 0);
    const historyRows = this.history
      .slice(0, 6)
      .map(
        (match) => `
          <div class="profile-history-row">
            <span class="history-result history-result--${match.result}">${match.result[0]!.toUpperCase()}</span>
            <div><b>${escapeHtml(match.awayName)}</b><small>${new Date(match.playedAt).toLocaleDateString()}</small></div>
            <strong>${match.homeScore} – ${match.awayScore}</strong>
          </div>`
      )
      .join("");

    this.renderShell(
      "more",
      `
        <section class="page-heading profile-heading">
          <button class="text-back" data-route="more">${uiIcon("arrow")} More</button>
          <span class="eyebrow">PROFILE / LOCAL GUEST</span>
          <h1>${escapeHtml(this.session.displayName)}</h1>
          <p>Only local football progress is shown. No sensitive personal data is requested.</p>
        </section>
        <section class="profile-layout">
          <form class="profile-card" id="profile-form">
            <div class="profile-avatar">${escapeHtml(this.session.displayName.slice(0, 1).toUpperCase())}</div>
            <label><span>Display name</span><input id="profile-name" maxlength="24" value="${escapeHtml(this.session.displayName)}" /></label>
            <button class="button button--primary button--wide" type="submit">Save display name</button>
            <small>Guest ID stays on this device.</small>
          </form>
          <section class="profile-stats">
            <article><span>MATCHES</span><b>${played}</b></article>
            <article><span>WINS</span><b>${wins}</b></article>
            <article><span>DRAWS</span><b>${draws}</b></article>
            <article><span>GOALS</span><b>${goals}</b></article>
            <article><span>LOCAL XP</span><b>${this.session.xp}</b></article>
          </section>
          <article class="profile-history">
            <header><span class="eyebrow">MATCH HISTORY</span><h2>Recent form</h2></header>
            ${historyRows || `<div class="empty-state">${uiIcon("history")}<b>No results yet</b><span>Finish a match to build local form.</span></div>`}
          </article>
        </section>
      `
    );
    this.root.querySelector("#profile-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!this.session) return;
      const name =
        (this.root.querySelector("#profile-name") as HTMLInputElement).value.trim() ||
        "KipsPlayer";
      this.session.displayName = name.slice(0, 24);
      this.persistence.saveSession(this.session);
      this.showToast("Profile saved");
      window.setTimeout(() => this.renderProfile(), 300);
    });
  }

  private renderMore(): void {
    this.renderShell(
      "more",
      `
        <section class="page-heading page-heading--split">
          <div>
            <span class="eyebrow">MORE / PRODUCT DIRECTORY</span>
            <h1>Every destination tells the truth.</h1>
            <p>Working local tools are open. Service-dependent products remain visibly blocked with the exact dependency.</p>
          </div>
          <span class="status-pill">${uiIcon("shield")} LOCAL CORE</span>
        </section>
        <section class="more-grid">
          <button class="more-route-card" data-route="profile">
            <span class="card-icon">${uiIcon("profile")}</span><div><small>ACCOUNT</small><h2>Profile</h2><p>Local identity, safe statistics and match history.</p></div>${uiIcon("arrow")}
          </button>
          <button class="more-route-card" data-route="settings">
            <span class="card-icon">${uiIcon("settings")}</span><div><small>DEVICE</small><h2>Settings</h2><p>Graphics, controls and accessibility preferences.</p></div>${uiIcon("arrow")}
          </button>
          <button class="more-route-card" data-route="identity">
            <span class="card-icon">${uiIcon("shield")}</span><div><small>CREATOR</small><h2>Club Identity</h2><p>Name, monogram and original kit colors.</p></div>${uiIcon("arrow")}
          </button>
        </section>
        <section class="section-heading"><div><span class="eyebrow">SERVICE DIRECTORY</span><h2>Not exposed as fake features</h2></div></section>
        <section class="service-directory">
          <article><span>ONLINE</span><h3>Kips Ranked & Quick Rooms</h3><p>Blocked until regional authoritative match hosts, reconnect and signed results exist.</p><b>BLOCKED</b></article>
          <article><span>COMPETITION</span><h3>Tournaments & Leagues</h3><p>Planned after schedule, bracket, roles, moderation and save/resume are integrated.</p><b>PLANNED</b></article>
          <article><span>ECONOMY</span><h3>Market & Store</h3><p>Hidden until an authoritative ledger, receipts and owner-approved cosmetic policy exist.</p><b>PLANNED</b></article>
          <article><span>CREATOR</span><h3>Player & Stadium Studio</h3><p>Planned after validated asset formats, moderation and performance budgets exist.</p><b>PLANNED</b></article>
        </section>
      `
    );
  }

  private clubTabs(active: "club" | "players" | "tactics" | "identity"): string {
    const tabs: Array<[RouteId, string]> = [
      ["club", "Squad"],
      ["players", "Players"],
      ["tactics", "Tactics"],
      ["identity", "Identity"]
    ];
    return `
      <nav class="club-tabs" aria-label="Club sections">
        ${tabs
          .map(
            ([route, label]) =>
              `<button data-route="${route}" class="${route === active ? "is-active" : ""}" aria-current="${route === active ? "page" : "false"}>${label}</button>`
          )
          .join("")}
      </nav>`;
  }

  private bindPlayerLinks(): void {
    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-player]")) {
      button.addEventListener("click", () => {
        const selectedPlayerId = button.dataset.player;
        if (!selectedPlayerId || !playerById(selectedPlayerId)) return;
        this.clubPlan = { ...this.clubPlan, selectedPlayerId };
        this.persistence.saveClubPlan(this.clubPlan);
        this.router.navigate("player");
      });
    }
  }

  private renderSettings(): void {
    const settings = this.settings;
    this.renderShell(
      "more",
      `
        <section class="page-heading page-heading--split">
          <div>
            <span class="eyebrow">SETTINGS / DEVICE PROFILE</span>
            <h1>Make the match yours.</h1>
            <p>Graphics and accessibility choices persist locally and apply to the next match load.</p>
          </div>
          <span class="status-pill status-pill--ready">${uiIcon("check")} SAVED LOCALLY</span>
        </section>
        <form class="settings-grid" id="settings-form">
          <section class="settings-card">
            <header class="settings-card__header">
              <span class="card-icon">${uiIcon("device")}</span>
              <div><small>DISPLAY</small><h2>Graphics</h2></div>
            </header>
            <label>
              <span>Quality preset</span>
              <select id="graphics-preset">
                ${["auto", "low", "balanced", "high"]
                  .map(
                    (value) =>
                      `<option value="${value}" ${settings.graphicsPreset === value ? "selected" : ""}>${value[0]!.toUpperCase()}${value.slice(1)}</option>`
                  )
                  .join("")}
              </select>
            </label>
            <label>
              <span>Match camera</span>
              <select id="camera-preset">
                ${[
                  ["broadcast", "Kips Broadcast"],
                  ["dynamic", "Dynamic Broadcast"],
                  ["tactical", "Tactical Wide"],
                  ["player", "Player Focus"]
                ]
                  .map(
                    ([value, label]) =>
                      `<option value="${value}" ${settings.cameraPreset === value ? "selected" : ""}>${label}</option>`
                  )
                  .join("")}
              </select>
            </label>
            <label>
              <span>Ball visibility</span>
              <select id="ball-visibility">
                <option value="high" ${settings.ballVisibility === "high" ? "selected" : ""}>High visibility</option>
                <option value="authentic" ${settings.ballVisibility === "authentic" ? "selected" : ""}>Authentic scale</option>
              </select>
            </label>
            <div class="graphics-profile">
              <div><span>RECOMMENDED</span><b>${this.capabilities?.recommendedPreset.toUpperCase() ?? "NOT MEASURED"}</b></div>
              <div><span>RENDERER</span><b>WEBGL 2</b></div>
              <div><span>FRAME TARGET</span><b>30 FPS LOW</b></div>
            </div>
          </section>
          <section class="settings-card">
            <header class="settings-card__header">
              <span class="card-icon">${uiIcon("shield")}</span>
              <div><small>ACCESS</small><h2>Accessibility</h2></div>
            </header>
            <label class="switch-row">
              <span><b>Reduced motion</b><small>Shorter presentation and UI movement</small></span>
              <input type="checkbox" id="reduced-motion" ${settings.reducedMotion ? "checked" : ""} />
            </label>
            <label class="switch-row">
              <span><b>Camera shake</b><small>Reserved for future validated effects</small></span>
              <input type="checkbox" id="camera-shake" ${settings.cameraShake ? "checked" : ""} />
            </label>
            <label class="switch-row">
              <span><b>Left-handed controls</b><small>Mirror action cluster and joystick</small></span>
              <input type="checkbox" id="left-handed" ${settings.leftHanded ? "checked" : ""} />
            </label>
            <label class="switch-row">
              <span><b>Floating joystick</b><small>Place movement control under the first touch</small></span>
              <input type="checkbox" id="floating-joystick" ${settings.floatingJoystick ? "checked" : ""} />
            </label>
            <label class="switch-row">
              <span><b>Tactical radar</b><small>Keep the radar as a secondary view</small></span>
              <input type="checkbox" id="radar-enabled" ${settings.radarEnabled ? "checked" : ""} />
            </label>
            <label>
              <span>Control scale</span>
              <input type="range" id="control-scale" min="0.85" max="1.25" step="0.05" value="${settings.controlScale}" />
            </label>
            <label>
              <span>Control opacity</span>
              <input type="range" id="control-opacity" min="0.4" max="1" step="0.05" value="${settings.controlOpacity}" />
            </label>
          </section>
          <div class="settings-actions">
            <button class="button button--primary" type="submit"><span>Save settings</span>${uiIcon("check")}</button>
            <button class="button button--quiet" type="button" id="reset-settings">Reset</button>
          </div>
        </form>
      `
    );

    this.root.querySelector("#settings-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      this.settings = {
        schemaVersion: 1,
        graphicsPreset: (this.root.querySelector("#graphics-preset") as HTMLSelectElement)
          .value as GameSettings["graphicsPreset"],
        reducedMotion: (this.root.querySelector("#reduced-motion") as HTMLInputElement).checked,
        cameraShake: (this.root.querySelector("#camera-shake") as HTMLInputElement).checked,
        cameraPreset: (this.root.querySelector("#camera-preset") as HTMLSelectElement)
          .value as GameSettings["cameraPreset"],
        leftHanded: (this.root.querySelector("#left-handed") as HTMLInputElement).checked,
        controlScale: Number(
          (this.root.querySelector("#control-scale") as HTMLInputElement).value
        ),
        controlOpacity: Number(
          (this.root.querySelector("#control-opacity") as HTMLInputElement).value
        ),
        floatingJoystick: (this.root.querySelector("#floating-joystick") as HTMLInputElement)
          .checked,
        radarEnabled: (this.root.querySelector("#radar-enabled") as HTMLInputElement).checked,
        ballVisibility: (this.root.querySelector("#ball-visibility") as HTMLSelectElement)
          .value as GameSettings["ballVisibility"]
      };
      this.persistence.saveSettings(this.settings);
      document.documentElement.classList.toggle("reduced-motion", this.settings.reducedMotion);
      this.showToast("Settings saved");
    });
    this.root.querySelector("#reset-settings")?.addEventListener("click", () => {
      this.settings = { ...DEFAULT_SETTINGS };
      this.persistence.saveSettings(this.settings);
      this.renderSettings();
    });
  }

  private async renderMatch(): Promise<void> {
    if (!this.capabilities) return;
    if (!this.activeMatch) {
      this.activeMatch = await this.persistence.getActiveMatch();
    }
    if (!this.activeMatch) {
      this.router.navigate("setup", true);
      return;
    }

    this.cleanupMatch();
    this.simulation = new MatchSimulation(this.activeMatch.config, this.activeMatch);
    const leftHandedClass = this.settings.leftHanded ? " match-shell--left-handed" : "";
    const floatingClass = this.settings.floatingJoystick ? " match-shell--floating-stick" : "";
    const radarClass = this.settings.radarEnabled ? "" : " match-shell--radar-hidden";
    this.root.innerHTML = `
      <main class="match-shell${leftHandedClass}${floatingClass}${radarClass}" id="match-shell" style="--control-scale:${this.settings.controlScale};--control-opacity:${this.settings.controlOpacity}">
        <canvas id="match-canvas" aria-label="Real-time 3D football match"></canvas>
        <div class="match-loading" id="match-loading">
          <div class="loading-ring"></div>
          <span class="eyebrow">KIPSBALL MATCHDAY</span>
          <strong>Loading 3D match</strong>
          <span id="match-loading-label">Preparing stadium</span>
          <div class="mini-progress"><i id="match-loading-bar"></i></div>
        </div>
        <header class="match-hud" aria-label="Match score">
          <div class="competition-bug">
            <img src="/icons/kipsball-mark.svg" alt="" />
            <span><small>LOCAL</small><b>EXHIBITION</b></span>
          </div>
          <div class="scoreboard">
            <span id="home-short" style="--team-color:${escapeHtml(this.activeMatch.config.homeColor)}">${escapeHtml(this.activeMatch.config.homeShortName)}</span>
            <b id="score">${this.activeMatch.homeScore}&nbsp;–&nbsp;${this.activeMatch.awayScore}</b>
            <span id="away-short" style="--team-color:${escapeHtml(this.activeMatch.config.awayColor)}">${escapeHtml(this.activeMatch.config.awayShortName)}</span>
          </div>
          <div class="match-clock"><span id="half-label">1ST</span><b id="clock">00:00</b></div>
          <button class="hud-button hud-button--pause" id="pause-match" aria-label="Pause match">
            <i></i><i></i><span>Pause</span>
          </button>
        </header>
        <div class="event-ribbon" id="event-ribbon" aria-live="polite"></div>
        <nav class="quick-tactics" aria-label="Team mentality">
          <span>MENTALITY</span>
          <button data-mentality="defensive" aria-label="Defensive mentality">DEF</button>
          <button class="is-active" data-mentality="balanced" aria-label="Balanced mentality">BAL</button>
          <button data-mentality="attacking" aria-label="Attacking mentality">ATT</button>
        </nav>
        <aside class="active-player-panel" aria-live="polite">
          <div class="active-player-number" id="active-player-number">10</div>
          <div>
            <span id="active-player-role">CM · ACTIVE PLAYER</span>
            <b id="active-player-name">Jaya</b>
            <i class="stamina-track"><span id="active-player-stamina"></span></i>
          </div>
        </aside>
        <canvas class="radar" id="radar" width="300" height="176" aria-label="Tactical radar"></canvas>
        <section class="mobile-controls" aria-label="Touch controls">
          <div class="joystick-zone" id="joystick-zone" role="application" aria-label="Movement joystick area">
            <div class="joystick" id="joystick">
              <span>MOVE</span><i class="joystick-ring"></i><b id="joystick-knob"></b>
            </div>
          </div>
          <div class="action-cluster">
            <button class="action-button action-button--north" data-control="through-switch" aria-label="Through pass or switch player">
              <i class="action-glyph action-glyph--lane"></i>
              <span class="label-attack">THROUGH</span><span class="label-defend">SWITCH</span>
            </button>
            <button class="action-button action-button--west" data-control="pass-second" aria-label="Pass or second defender">
              <i class="action-glyph action-glyph--pass"></i>
              <span class="label-attack">PASS</span><span class="label-defend">2ND DEF</span>
            </button>
            <button class="action-button action-button--east action-button--primary" data-control="shoot-slide" aria-label="Shoot or slide tackle">
              <i class="action-glyph action-glyph--target"></i>
              <span class="label-attack">SHOOT</span><span class="label-defend">SLIDE</span>
            </button>
            <button class="action-button action-button--south" data-control="sprint-tackle" aria-label="Sprint and skill or sprint and tackle">
              <i class="action-glyph action-glyph--burst"></i>
              <span class="label-attack">SPRINT · SKILL</span><span class="label-defend">SPRINT · TACKLE</span>
            </button>
          </div>
        </section>
        <div class="rotate-device" id="rotate-device">
          <img src="/icons/kipsball-mark.svg" alt="" />
          <h2>Rotate to landscape</h2>
          <p>The match uses the whole landscape viewport instead of shrinking into a portrait strip.</p>
          <button class="button button--quiet" id="adaptive-view">Use adaptive view</button>
        </div>
        <div class="match-modal is-hidden" id="match-modal"></div>
      </main>
    `;

    const shell = this.root.querySelector("#match-shell") as HTMLElement;
    const canvas = this.root.querySelector("#match-canvas") as HTMLCanvasElement;
    const loadingBar = this.root.querySelector("#match-loading-bar") as HTMLElement;
    const loadingLabel = this.root.querySelector("#match-loading-label") as HTMLElement;

    const { MatchRenderer } = await import("../game/renderer");
    this.renderer = new MatchRenderer(
      shell,
      canvas,
      this.settings,
      this.capabilities.recommendedPreset
    );

    try {
      await this.renderer.initialize(this.simulation.snapshot(), (progress, label) => {
        loadingBar.style.width = `${Math.round(progress * 100)}%`;
        loadingLabel.textContent = label;
      });
      this.root.querySelector("#match-loading")?.classList.add("is-hidden");
      this.bindMatchInput();
      this.showPresentation();
      this.startMatchLoop();
    } catch (error) {
      const detail = error instanceof Error ? error.message : "3D asset load failed";
      this.showMatchError(detail);
    }

    this.root.querySelector("#adaptive-view")?.addEventListener("click", () => {
      this.root.querySelector("#rotate-device")?.classList.add("force-hidden");
    });
  }

  private showPresentation(): void {
    if (!this.simulation || !this.activeMatch) return;
    const modal = this.root.querySelector("#match-modal") as HTMLElement;
    modal.classList.remove("is-hidden");
    modal.innerHTML = `
      <section class="presentation-card">
        <button class="skip-presentation" id="skip-presentation">Skip <span>›</span></button>
        <div class="presentation-brand"><img src="/icons/kipsball-mark.svg" alt="" /><span>KIPSBALL</span></div>
        <span class="eyebrow">KIPS STADIUM · CLEAR DAY · LOCAL EXHIBITION</span>
        <div class="presentation-matchup">
          <article>
            <div class="team-crest" style="--club:${escapeHtml(this.activeMatch.config.homeColor)}">${escapeHtml(this.activeMatch.config.homeShortName)}</div>
            <span>HOME</span>
            <h1>${escapeHtml(this.activeMatch.config.homeName)}</h1>
          </article>
          <div class="presentation-vs"><small>MATCHDAY</small><b>VS</b><span>${Math.round(this.activeMatch.config.realDurationSeconds / 60)} MIN</span></div>
          <article>
            <div class="team-crest" style="--club:${escapeHtml(this.activeMatch.config.awayColor)}">${escapeHtml(this.activeMatch.config.awayShortName)}</div>
            <span>AWAY</span>
            <h2>${escapeHtml(this.activeMatch.config.awayName)}</h2>
          </article>
        </div>
        <div class="presentation-footer"><span>${uiIcon("tactics")} ${this.activeMatch.config.homeFormation ?? "4-3-3"}</span><b>READY FOR KICKOFF</b><span>${uiIcon("stadium")} BROADCAST</span></div>
      </section>
    `;
    const start = (): void => {
      window.clearTimeout(this.presentationTimer);
      modal.classList.add("is-hidden");
      this.simulation?.startFirstHalf();
    };
    modal.querySelector("#skip-presentation")?.addEventListener("click", start);
    this.presentationTimer = window.setTimeout(start, this.settings.reducedMotion ? 700 : 4200);
  }

  private bindMatchInput(): void {
    const input = this.matchInput();
    const pressed = new Set<string>();
    let touchSprint = false;
    let touchSecondDefender = false;
    let keyboardMovementActive = false;
    const keyDown = (event: KeyboardEvent): void => {
      const key = event.key.toLowerCase();
      const controlled = new Set([
        "arrowup",
        "arrowdown",
        "arrowleft",
        "arrowright",
        " ",
        "shift",
        "j",
        "k",
        "l",
        "u",
        "i",
        "o",
        "q",
        "w",
        "a",
        "s",
        "d"
      ]);
      if (controlled.has(key)) event.preventDefault();
      pressed.add(key);
      if (event.repeat) return;
      if (key === "j") input.pass = true;
      if (key === "u") input.through = true;
      if (key === "k") input.shoot = true;
      if (key === "l") input.tackle = true;
      if (key === "i") input.slide = true;
      if (key === "q") input.skill = true;
      if (event.key === " ") input.switchPlayer = true;
      if (event.key === "Escape") this.togglePause();
    };
    const keyUp = (event: KeyboardEvent): void => {
      pressed.delete(event.key.toLowerCase());
    };
    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp);
    this.cleanupHandlers.push(
      () => window.removeEventListener("keydown", keyDown),
      () => window.removeEventListener("keyup", keyUp)
    );

    const updateKeyboard = (): void => {
      const horizontal =
        Number(pressed.has("d") || pressed.has("arrowright")) -
        Number(pressed.has("a") || pressed.has("arrowleft"));
      const vertical =
        Number(pressed.has("s") || pressed.has("arrowdown")) -
        Number(pressed.has("w") || pressed.has("arrowup"));
      if (horizontal || vertical) {
        input.moveX = horizontal;
        input.moveZ = vertical;
        keyboardMovementActive = true;
      } else if (keyboardMovementActive) {
        input.moveX = 0;
        input.moveZ = 0;
        keyboardMovementActive = false;
      }
      input.sprint = pressed.has("shift") || touchSprint;
      input.secondDefender = pressed.has("o") || touchSecondDefender;
    };
    this.cleanupHandlers.push(() => {
      pressed.clear();
    });
    (this.root.querySelector("#match-shell") as HTMLElement).dataset.keyboardUpdater = "bound";
    this.keyboardUpdater = updateKeyboard;

    const joystickZone = this.root.querySelector("#joystick-zone") as HTMLElement;
    const joystick = this.root.querySelector("#joystick") as HTMLElement;
    const knob = this.root.querySelector("#joystick-knob") as HTMLElement;
    let joystickPointer: number | null = null;
    let joystickOriginX = 0;
    let joystickOriginY = 0;
    const updateJoystick = (event: PointerEvent): void => {
      const radius = Math.max(28, joystick.offsetWidth * 0.31);
      let x = event.clientX - joystickOriginX;
      let y = event.clientY - joystickOriginY;
      const magnitude = Math.hypot(x, y);
      if (magnitude > radius) {
        x = (x / magnitude) * radius;
        y = (y / magnitude) * radius;
      }
      knob.style.transform = `translate(${x}px, ${y}px)`;
      input.moveX = x / radius;
      input.moveZ = y / radius;
    };
    const resetJoystick = (): void => {
      joystickPointer = null;
      knob.style.transform = "translate(0, 0)";
      joystickZone.classList.remove("is-active");
      input.moveX = 0;
      input.moveZ = 0;
    };
    joystickZone.addEventListener("pointerdown", (event) => {
      joystickPointer = event.pointerId;
      joystickZone.setPointerCapture(event.pointerId);
      const zoneRect = joystickZone.getBoundingClientRect();
      if (this.settings.floatingJoystick) {
        const half = joystick.offsetWidth / 2;
        const localX = Math.min(zoneRect.width - half, Math.max(half, event.clientX - zoneRect.left));
        const localY = Math.min(zoneRect.height - half, Math.max(half, event.clientY - zoneRect.top));
        joystick.style.left = `${localX}px`;
        joystick.style.top = `${localY}px`;
        joystickOriginX = zoneRect.left + localX;
        joystickOriginY = zoneRect.top + localY;
      } else {
        const rect = joystick.getBoundingClientRect();
        joystickOriginX = rect.left + rect.width / 2;
        joystickOriginY = rect.top + rect.height / 2;
      }
      joystickZone.classList.add("is-active");
      updateJoystick(event);
    });
    joystickZone.addEventListener("pointermove", (event) => {
      if (event.pointerId === joystickPointer) updateJoystick(event);
    });
    joystickZone.addEventListener("pointerup", resetJoystick);
    joystickZone.addEventListener("pointercancel", resetJoystick);

    const isAttacking = (): boolean => {
      const snapshot = this.simulation?.snapshot();
      if (!snapshot) return true;
      const owner = snapshot.ball.controlledBy;
      if (owner) return owner.startsWith("home-");
      return snapshot.ball.lastTouch?.startsWith("home-") ?? true;
    };
    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-control]")) {
      const control = button.dataset.control;
      let pointerId: number | null = null;
      let pressedAt = 0;
      button.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        pointerId = event.pointerId;
        pressedAt = performance.now();
        button.setPointerCapture(event.pointerId);
        button.classList.add("is-pressed");
        const attacking = isAttacking();
        if (control === "through-switch" && !attacking) input.switchPlayer = true;
        if (control === "pass-second" && !attacking) {
          touchSecondDefender = true;
          input.secondDefender = true;
        }
        if (control === "shoot-slide" && !attacking) input.slide = true;
        if (control === "sprint-tackle") {
          touchSprint = true;
          input.sprint = true;
        }
      });
      const release = (event: PointerEvent, cancelled = false): void => {
        if (pointerId !== event.pointerId) return;
        const duration = performance.now() - pressedAt;
        const power = Math.min(1, 0.25 + duration / 900);
        const attacking = isAttacking();
        button.classList.remove("is-pressed");
        if (!cancelled) {
          if (control === "through-switch" && attacking) {
            input.throughPower = power;
            input.through = true;
          } else if (control === "pass-second" && attacking) {
            input.passPower = power;
            input.pass = true;
          } else if (control === "shoot-slide" && attacking) {
            input.shotPower = power;
            input.shoot = true;
          } else if (control === "sprint-tackle" && duration < 240) {
            if (attacking) input.skill = true;
            else input.tackle = true;
          }
        }
        if (control === "pass-second") {
          touchSecondDefender = false;
          input.secondDefender = pressed.has("o");
        }
        if (control === "sprint-tackle") {
          touchSprint = false;
          input.sprint = pressed.has("shift");
        }
        pointerId = null;
      };
      button.addEventListener("pointerup", (event) => release(event));
      button.addEventListener("pointercancel", (event) => release(event, true));
    }

    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-mentality]")) {
      button.addEventListener("click", () => {
        const mentality = button.dataset.mentality as MatchSnapshot["mentality"];
        this.simulation?.setMentality(mentality);
      });
    }
    this.root.querySelector("#pause-match")?.addEventListener("click", () => this.togglePause());
  }

  private keyboardUpdater: (() => void) | null = null;
  private inputState: MatchInput = { ...EMPTY_INPUT };

  private matchInput(): MatchInput {
    return this.inputState;
  }

  private startMatchLoop(): void {
    if (!this.simulation || !this.renderer) return;
    let lastTime = performance.now();
    let accumulator = 0;
    let lastPersist = lastTime;
    let lastHudUpdate = 0;
    let lastMetricsUpdate = 0;
    let lastEventId = 0;

    const frame = (time: number): void => {
      if (!this.simulation || !this.renderer) return;
      const frameSeconds = Math.min(0.1, (time - lastTime) / 1000);
      lastTime = time;
      accumulator += frameSeconds;
      this.keyboardUpdater?.();

      if (!this.matchPaused) {
        let consumedAction = false;
        while (accumulator >= MatchSimulation.stepSeconds) {
          this.simulation.tick(MatchSimulation.stepSeconds, this.inputState);
          if (!consumedAction) {
            this.inputState.pass = false;
            this.inputState.through = false;
            this.inputState.shoot = false;
            this.inputState.tackle = false;
            this.inputState.slide = false;
            this.inputState.skill = false;
            this.inputState.switchPlayer = false;
            consumedAction = true;
          }
          accumulator -= MatchSimulation.stepSeconds;
        }
      }

      const snapshot = this.simulation.snapshot();
      this.activeMatch = snapshot;
      this.renderer.render(snapshot);

      if (time - lastHudUpdate > 80) {
        this.updateMatchHud(snapshot);
        this.drawRadar(snapshot);
        lastHudUpdate = time;
      }
      if (time - lastMetricsUpdate > 1000) {
        const shell = this.root.querySelector("#match-shell") as HTMLElement | null;
        if (shell) shell.dataset.rendererMetrics = JSON.stringify(this.renderer.metrics());
        lastMetricsUpdate = time;
      }

      const latestEvent = snapshot.events.at(-1);
      if (latestEvent && latestEvent.id !== lastEventId) {
        lastEventId = latestEvent.id;
        this.showMatchEvent(latestEvent.label);
      }

      if (time - lastPersist > 5000) {
        lastPersist = time;
        void this.persistence.saveActiveMatch(snapshot);
      }

      if (snapshot.phase === "halftime") this.showHalfTime(snapshot);
      if (snapshot.phase === "fulltime") {
        void this.finalizeMatch(snapshot);
        return;
      }

      this.animationFrame = window.requestAnimationFrame(frame);
    };
    this.animationFrame = window.requestAnimationFrame(frame);
  }

  private updateMatchHud(snapshot: MatchSnapshot): void {
    const score = this.root.querySelector("#score");
    const clock = this.root.querySelector("#clock");
    const half = this.root.querySelector("#half-label");
    const shell = this.root.querySelector("#match-shell");
    const active = snapshot.players.find((player) => player.id === snapshot.activePlayerId);
    const ballOwner = snapshot.ball.controlledBy;
    const homeAttacking = ballOwner
      ? ballOwner.startsWith("home-")
      : snapshot.ball.lastTouch?.startsWith("home-") ?? true;
    shell?.classList.toggle("is-attacking", homeAttacking);
    shell?.classList.toggle("is-defending", !homeAttacking);
    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-mentality]")) {
      const selected = button.dataset.mentality === snapshot.mentality;
      button.classList.toggle("is-active", selected);
      button.setAttribute("aria-pressed", String(selected));
    }
    if (score) score.innerHTML = `${snapshot.homeScore}&nbsp;–&nbsp;${snapshot.awayScore}`;
    if (clock) clock.textContent = formatClock(snapshot.clockSeconds);
    if (half) {
      half.textContent =
        snapshot.phase === "first-half"
          ? "1ST"
          : snapshot.phase === "second-half"
            ? "2ND"
            : snapshot.phase === "halftime"
              ? "HT"
              : "FT";
    }
    if (active) {
      const playerName =
        active.displayName ??
        PLAYER_NAMES.find(([number]) => Number(number) === active.shirtNumber)?.[1] ??
        `Player ${active.shirtNumber}`;
      const number = this.root.querySelector("#active-player-number");
      const role = this.root.querySelector("#active-player-role");
      const name = this.root.querySelector("#active-player-name");
      const stamina = this.root.querySelector("#active-player-stamina") as HTMLElement | null;
      if (number) number.textContent = String(active.shirtNumber);
      if (role) role.textContent = `${active.role} · ACTIVE PLAYER`;
      if (name) name.textContent = playerName;
      if (stamina) stamina.style.width = `${Math.round(active.stamina * 100)}%`;
    }
  }

  private drawRadar(snapshot: MatchSnapshot): void {
    if (!this.settings.radarEnabled) return;
    const canvas = this.root.querySelector("#radar") as HTMLCanvasElement | null;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const width = canvas.width;
    const height = canvas.height;
    context.clearRect(0, 0, width, height);
    context.fillStyle = "rgba(5, 24, 17, .78)";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "rgba(231, 247, 228, .48)";
    context.lineWidth = 2;
    context.strokeRect(5, 5, width - 10, height - 10);
    context.beginPath();
    context.moveTo(width / 2, 5);
    context.lineTo(width / 2, height - 5);
    context.stroke();

    const project = (x: number, z: number): [number, number] => [
      5 + ((x + PITCH.halfLength) / PITCH.length) * (width - 10),
      5 + ((z + PITCH.halfWidth) / PITCH.width) * (height - 10)
    ];

    for (const player of snapshot.players) {
      const [x, y] = project(player.x, player.z);
      context.beginPath();
      if (player.id === snapshot.activePlayerId) {
        context.fillStyle = "#f6ff79";
        context.moveTo(x, y - 5);
        context.lineTo(x - 4.5, y + 4);
        context.lineTo(x + 4.5, y + 4);
        context.closePath();
      } else {
        context.fillStyle = player.team === "home" ? snapshot.config.homeColor : snapshot.config.awayColor;
        context.arc(x, y, player.role === "GK" ? 4 : 3, 0, Math.PI * 2);
      }
      context.fill();
    }
    const [ballX, ballY] = project(snapshot.ball.x, snapshot.ball.z);
    context.beginPath();
    context.fillStyle = "#ffffff";
    context.arc(ballX, ballY, 2.4, 0, Math.PI * 2);
    context.fill();
  }

  private showMatchEvent(label: string): void {
    const ribbon = this.root.querySelector("#event-ribbon");
    if (!ribbon) return;
    ribbon.textContent = label;
    ribbon.classList.add("is-visible");
    window.setTimeout(() => ribbon.classList.remove("is-visible"), 1200);
  }

  private togglePause(): void {
    if (!this.simulation) return;
    const current = this.simulation.snapshot();
    if (current.phase === "presentation" || current.phase === "halftime") return;
    this.matchPaused = !this.matchPaused;
    const modal = this.root.querySelector("#match-modal") as HTMLElement | null;
    if (!modal) return;
    if (!this.matchPaused) {
      modal.classList.add("is-hidden");
      delete modal.dataset.phase;
      return;
    }
    const snapshot = current;
    modal.dataset.phase = "pause";
    modal.classList.remove("is-hidden");
    modal.innerHTML = `
      <section class="pause-card">
        <header class="pause-card__header">
          <div class="modal-mark"><img src="/icons/kipsball-mark.svg" alt="" /></div>
          <div>
            <span class="eyebrow">LOCAL MATCH / PAUSED</span>
            <h1>${snapshot.config.homeShortName} ${snapshot.homeScore}–${snapshot.awayScore} ${snapshot.config.awayShortName}</h1>
            <p>${formatClock(snapshot.clockSeconds)} · ${snapshot.phase === "first-half" ? "First half" : "Second half"}</p>
          </div>
        </header>
        <div class="pause-grid">
          <section>
            <span class="pause-label">TEAM MENTALITY</span>
            <div class="pause-segments">
              ${(["defensive", "balanced", "attacking"] as const)
                .map(
                  (value) =>
                    `<button data-pause-mentality="${value}" class="${snapshot.mentality === value ? "is-active" : ""}">${value === "defensive" ? "Protect" : value === "attacking" ? "Attack" : "Balanced"}</button>`
                )
                .join("")}
            </div>
          </section>
          <section>
            <span class="pause-label">CAMERA</span>
            <div class="pause-segments pause-segments--wrap">
              ${([
                ["broadcast", "Broadcast"],
                ["dynamic", "Dynamic"],
                ["tactical", "Wide"],
                ["player", "Player"]
              ] as const)
                .map(
                  ([value, label]) =>
                    `<button data-pause-camera="${value}" class="${this.settings.cameraPreset === value ? "is-active" : ""}">${label}</button>`
                )
                .join("")}
            </div>
          </section>
          <section class="pause-controls-guide">
            <span class="pause-label">LIVE CONTROLS</span>
            <p><b>Attack:</b> Through · Pass · Shoot · Sprint / tap Skill</p>
            <p><b>Defend:</b> Switch · Hold 2nd Defender · Slide · Sprint / tap Tackle</p>
          </section>
          <section class="pause-toggle-row">
            <span><b>Tactical radar</b><small>Secondary view only</small></span>
            <button id="pause-radar" aria-pressed="${this.settings.radarEnabled}">${this.settings.radarEnabled ? "ON" : "OFF"}</button>
          </section>
        </div>
        <div class="pause-actions">
          <button class="button button--primary" id="resume-match"><span>Resume match</span>${uiIcon("arrow")}</button>
          <button class="button button--quiet" id="save-exit">Save & exit to Home</button>
        </div>
      </section>
    `;
    for (const button of modal.querySelectorAll<HTMLButtonElement>("[data-pause-mentality]")) {
      button.addEventListener("click", () => {
        const mentality = button.dataset.pauseMentality as MatchSnapshot["mentality"];
        this.simulation?.setMentality(mentality);
        for (const sibling of modal.querySelectorAll("[data-pause-mentality]")) {
          sibling.classList.toggle(
            "is-active",
            (sibling as HTMLElement).dataset.pauseMentality === mentality
          );
        }
      });
    }
    for (const button of modal.querySelectorAll<HTMLButtonElement>("[data-pause-camera]")) {
      button.addEventListener("click", () => {
        const camera = button.dataset.pauseCamera as GameSettings["cameraPreset"];
        this.settings = { ...this.settings, cameraPreset: camera };
        this.persistence.saveSettings(this.settings);
        this.renderer?.setCameraPreset(camera);
        for (const sibling of modal.querySelectorAll("[data-pause-camera]")) {
          sibling.classList.toggle(
            "is-active",
            (sibling as HTMLElement).dataset.pauseCamera === camera
          );
        }
      });
    }
    modal.querySelector("#pause-radar")?.addEventListener("click", (event) => {
      this.settings = { ...this.settings, radarEnabled: !this.settings.radarEnabled };
      this.persistence.saveSettings(this.settings);
      this.root
        .querySelector("#match-shell")
        ?.classList.toggle("match-shell--radar-hidden", !this.settings.radarEnabled);
      const button = event.currentTarget as HTMLButtonElement;
      button.textContent = this.settings.radarEnabled ? "ON" : "OFF";
      button.setAttribute("aria-pressed", String(this.settings.radarEnabled));
    });
    modal.querySelector("#resume-match")?.addEventListener("click", () => this.togglePause());
    modal.querySelector("#save-exit")?.addEventListener("click", () => {
      if (!this.simulation) return;
      void this.persistence.saveActiveMatch(this.simulation.snapshot()).then(() => {
        this.router.navigate("home");
      });
    });
  }

  private showHalfTime(snapshot: MatchSnapshot, force = false): void {
    const modal = this.root.querySelector("#match-modal") as HTMLElement | null;
    if (!modal || (!force && modal.dataset.phase === "halftime")) return;
    modal.dataset.phase = "halftime";
    const possession = percentage(
      snapshot.stats.homePossessionTicks,
      snapshot.stats.awayPossessionTicks
    );
    const availableBench = snapshot.config.homeBench ?? [];
    const homeOutfield = snapshot.players.filter(
      (player) => player.team === "home" && player.role !== "GK"
    );
    const substitutionsLeft = Math.max(0, 3 - snapshot.substitutions.length);
    modal.classList.remove("is-hidden");
    modal.innerHTML = `
      <section class="halftime-card">
        <div class="modal-mark"><img src="/icons/kipsball-mark.svg" alt="" /></div>
        <span class="eyebrow">MATCHDAY / HALF-TIME</span>
        <h1>${snapshot.config.homeShortName} ${snapshot.homeScore}–${snapshot.awayScore} ${snapshot.config.awayShortName}</h1>
        <div class="match-facts">
          <div><span>${snapshot.stats.homeShots}</span><b>Shots</b><span>${snapshot.stats.awayShots}</span></div>
          <div><span>${possession[0]}%</span><b>Possession</b><span>${possession[1]}%</span></div>
          <div><span>${snapshot.stats.homePasses}</span><b>Passes</b><span>${snapshot.stats.awayPasses}</span></div>
        </div>
        <div class="halftime-management">
          <section>
            <span class="pause-label">MENTALITY</span>
            <div class="pause-segments">
              ${(["defensive", "balanced", "attacking"] as const)
                .map(
                  (value) =>
                    `<button data-half-mentality="${value}" class="${snapshot.mentality === value ? "is-active" : ""}">${value === "defensive" ? "Protect" : value === "attacking" ? "Attack" : "Balanced"}</button>`
                )
                .join("")}
            </div>
          </section>
          <section class="substitution-panel">
            <div class="substitution-panel__title">
              <span class="pause-label">SUBSTITUTION</span>
              <b>${snapshot.substitutions.length}/3 USED</b>
            </div>
            <div class="substitution-selects">
              <label><span>Player off</span><select id="sub-out" ${substitutionsLeft === 0 ? "disabled" : ""}>
                ${homeOutfield
                  .map(
                    (player) =>
                      `<option value="${player.id}">${escapeHtml(player.displayName ?? `Player ${player.shirtNumber}`)} · ${player.role} · ${Math.round(player.stamina * 100)}%</option>`
                  )
                  .join("")}
              </select></label>
              <label><span>Player on</span><select id="sub-in" ${substitutionsLeft === 0 ? "disabled" : ""}>
                ${availableBench
                  .map(
                    (player) =>
                      `<option value="${escapeHtml(player.profileId)}">${escapeHtml(player.displayName)} · #${player.number}</option>`
                  )
                  .join("")}
              </select></label>
              <button class="button button--quiet" id="confirm-sub" ${substitutionsLeft === 0 || availableBench.length === 0 ? "disabled" : ""}>Confirm change</button>
            </div>
            ${
              snapshot.substitutions.length
                ? `<ol class="substitution-log">${snapshot.substitutions
                    .map(
                      (item) =>
                        `<li><b>${escapeHtml(item.inName)}</b><span>for ${escapeHtml(item.outName)}</span></li>`
                    )
                    .join("")}</ol>`
                : `<p class="substitution-empty">No changes made. Up to three explicit changes are available.</p>`
            }
          </section>
        </div>
        <button class="button button--primary button--wide" id="second-half"><span>Start second half</span>${uiIcon("arrow")}</button>
      </section>
    `;
    for (const button of modal.querySelectorAll<HTMLButtonElement>("[data-half-mentality]")) {
      button.addEventListener("click", () => {
        const mentality = button.dataset.halfMentality as MatchSnapshot["mentality"];
        this.simulation?.setMentality(mentality);
        this.showHalfTime(this.simulation?.snapshot() ?? snapshot, true);
      });
    }
    modal.querySelector("#confirm-sub")?.addEventListener("click", () => {
      const outId = (modal.querySelector("#sub-out") as HTMLSelectElement | null)?.value;
      const inId = (modal.querySelector("#sub-in") as HTMLSelectElement | null)?.value;
      const incoming = this.simulation
        ?.snapshot()
        .config.homeBench?.find((player) => player.profileId === inId);
      if (!outId || !incoming || !this.simulation?.substitutePlayer(outId, incoming)) {
        this.showToast("Substitution could not be applied");
        return;
      }
      const updated = this.simulation.snapshot();
      this.activeMatch = updated;
      void this.persistence.saveActiveMatch(updated);
      this.showHalfTime(updated, true);
    });
    modal.querySelector("#second-half")?.addEventListener("click", () => {
      modal.classList.add("is-hidden");
      delete modal.dataset.phase;
      this.simulation?.startSecondHalf();
    });
  }

  private async finalizeMatch(snapshot: MatchSnapshot): Promise<void> {
    if (this.matchFinalizing) return;
    this.matchFinalizing = true;
    window.cancelAnimationFrame(this.animationFrame);
    this.lastRendererMetrics = this.renderer?.metrics() ?? null;
    const result: MatchSummary["result"] =
      snapshot.homeScore > snapshot.awayScore
        ? "win"
        : snapshot.homeScore < snapshot.awayScore
          ? "loss"
          : "draw";
    const summary: MatchSummary = {
      id: snapshot.config.id,
      playedAt: new Date().toISOString(),
      homeName: snapshot.config.homeName,
      awayName: snapshot.config.awayName,
      homeScore: snapshot.homeScore,
      awayScore: snapshot.awayScore,
      result,
      stats: snapshot.stats
    };
    this.lastSummary = summary;
    this.history.unshift(summary);
    this.history = this.history.slice(0, 20);
    await Promise.all([
      this.persistence.addMatchSummary(summary),
      this.persistence.clearActiveMatch()
    ]);
    this.activeMatch = null;
    if (this.session) {
      this.session.xp += result === "win" ? 160 : result === "draw" ? 100 : 70;
      this.persistence.saveSession(this.session);
    }
    this.router.navigate("results");
  }

  private renderResults(): void {
    const summary = this.lastSummary ?? this.history[0];
    if (!summary) {
      this.router.navigate("home", true);
      return;
    }
    const possession = percentage(
      summary.stats.homePossessionTicks,
      summary.stats.awayPossessionTicks
    );
    const resultLabel =
      summary.result === "win" ? "HOME WIN" : summary.result === "loss" ? "AWAY WIN" : "DRAW";
    const earnedXp = summary.result === "win" ? 160 : summary.result === "draw" ? 100 : 70;
    this.root.innerHTML = `
      <main class="results-screen screen-scroll">
        <div class="results-topbar">
          <div class="brand-lockup">
            <img src="/icons/kipsball-mark.svg" alt="" />
            <div><strong>KipsBall</strong><span>Matchday report</span></div>
          </div>
          <span>${uiIcon("check")} RESULT SAVED LOCALLY</span>
        </div>
        <header class="results-hero">
          <span class="eyebrow">FULL-TIME / KIPS STADIUM</span>
          <div class="results-score">
            <article><span>HOME</span><h2>${escapeHtml(summary.homeName)}</h2></article>
            <div><b>${summary.homeScore}<i>–</i>${summary.awayScore}</b><small>${resultLabel}</small></div>
            <article><span>AWAY</span><h2>${escapeHtml(summary.awayName)}</h2></article>
          </div>
          <div class="result-reward"><span>LOCAL PROGRESS</span><b>+${earnedXp} XP</b><small>No currency or online rank attached</small></div>
        </header>
        <section class="results-content">
          <article class="results-card results-card--facts">
            <header><span class="card-icon">${uiIcon("tactics")}</span><div><small>MATCH REPORT</small><h2>Match facts</h2></div></header>
            <div class="match-facts match-facts--results">
              <div><span>${summary.stats.homeShots}</span><b>Shots</b><span>${summary.stats.awayShots}</span></div>
              <div><span>${summary.stats.homeShotsOnTarget}</span><b>On target</b><span>${summary.stats.awayShotsOnTarget}</span></div>
              <div><span>${possession[0]}%</span><b>Possession</b><span>${possession[1]}%</span></div>
              <div><span>${summary.stats.homePasses}</span><b>Passes</b><span>${summary.stats.awayPasses}</span></div>
              <div><span>${summary.stats.homeTackles}</span><b>Tackles</b><span>${summary.stats.awayTackles}</span></div>
            </div>
          </article>
          <article class="results-card">
            <header><span class="card-icon">${uiIcon("device")}</span><div><small>RUNNING BUILD</small><h2>Renderer evidence</h2></div></header>
            ${
              this.lastRendererMetrics
                ? `<dl>
                    <div><dt>Draw calls</dt><dd>${this.lastRendererMetrics.drawCalls}</dd></div>
                    <div><dt>Triangles</dt><dd>${this.lastRendererMetrics.triangles.toLocaleString()}</dd></div>
                    <div><dt>GPU geometries</dt><dd>${this.lastRendererMetrics.geometries}</dd></div>
                    <div><dt>Pixel ratio</dt><dd>${this.lastRendererMetrics.pixelRatio.toFixed(2)}</dd></div>
                  </dl>`
                : `<p>Renderer counters were not captured for this restored result.</p>`
            }
          </article>
          <div class="results-actions">
            <button class="button button--primary" id="results-home"><span>Return Home</span>${uiIcon("arrow")}</button>
            <button class="button button--quiet" id="results-rematch">Set up rematch</button>
          </div>
        </section>
      </main>
    `;
    this.root.querySelector("#results-home")?.addEventListener("click", () => this.router.navigate("home"));
    this.root.querySelector("#results-rematch")?.addEventListener("click", () => this.router.navigate("setup"));
  }

  private showMatchError(detail: string): void {
    const loading = this.root.querySelector("#match-loading") as HTMLElement | null;
    if (!loading) return;
    loading.classList.remove("is-hidden");
    loading.innerHTML = `
      <section class="error-panel error-panel--match">
        <span class="eyebrow">3D RENDERER ERROR</span>
        <h1>Match not started</h1>
        <p>${escapeHtml(detail)}</p>
        <button class="button button--primary" id="retry-match">Retry</button>
        <button class="button button--quiet" id="leave-match">Return Home</button>
      </section>
    `;
    loading.querySelector("#retry-match")?.addEventListener("click", () => void this.renderMatch());
    loading.querySelector("#leave-match")?.addEventListener("click", () => this.router.navigate("home"));
  }

  private cleanupMatch(): void {
    window.cancelAnimationFrame(this.animationFrame);
    window.clearTimeout(this.presentationTimer);
    for (const cleanup of this.cleanupHandlers.splice(0)) cleanup();
    this.keyboardUpdater = null;
    this.inputState = { ...EMPTY_INPUT };
    this.renderer?.dispose();
    this.renderer = null;
    this.simulation = null;
    this.matchPaused = false;
    this.matchFinalizing = false;
  }

  private renderShell(
    active: "home" | "play" | "club" | "activities" | "more",
    content: string
  ): void {
    if (!this.session) return;
    const activeLabel = {
      home: "Home",
      play: "Play",
      club: "My Club",
      activities: "Activities",
      more: "More"
    }[active];
    this.root.innerHTML = `
      <main class="app-shell">
        <header class="topbar">
          <button class="brand-lockup brand-lockup--button" data-route="home" aria-label="KipsBall Home">
            <img src="/icons/kipsball-mark.svg" alt="" />
            <div><strong>KipsBall</strong><span>Kips Technology</span></div>
          </button>
          <div class="topbar-location"><span>LOCAL CORE</span><i></i><b>${activeLabel}</b></div>
          <div class="topbar-actions">
            <div class="connection-chip"><i></i><span>OFFLINE READY</span></div>
            <div class="xp-chip"><span>XP</span><b>${this.session.xp}</b></div>
            <div class="profile-chip">
            <i>${escapeHtml(this.session.displayName.slice(0, 1).toUpperCase())}</i>
            <span><b>${escapeHtml(this.session.displayName)}</b><small>Guest · Local</small></span>
            </div>
          </div>
        </header>
        <div class="shell-body">
          <aside class="sidebar" aria-label="Primary">
            <span class="sidebar-label">MENU</span>
            ${this.navButton("home", "Home", active)}
            ${this.navButton("play", "Play", active)}
            ${this.navButton("club", "Club", active)}
            ${this.navButton("activities", "Activities", active)}
            ${this.navButton("more", "More", active)}
            <div class="sidebar-build"><span>BUILD</span><b>CORE 0.3</b><small>Local</small></div>
          </aside>
          <section class="screen-scroll shell-content">${content}</section>
        </div>
        <nav class="bottom-nav" aria-label="Primary mobile navigation">
          ${this.navButton("home", "Home", active)}
          ${this.navButton("play", "Play", active)}
          ${this.navButton("club", "Club", active)}
          ${this.navButton("activities", "Activities", active)}
          ${this.navButton("more", "More", active)}
        </nav>
        <div class="toast" id="toast" role="status"></div>
      </main>
    `;
    for (const element of this.root.querySelectorAll<HTMLElement>("[data-route]")) {
      element.addEventListener("click", () => {
        const route = element.dataset.route as RouteId | undefined;
        if (route) this.router.navigate(route);
      });
    }
  }

  private navButton(
    route: "home" | "play" | "club" | "activities" | "more",
    label: string,
    active: string
  ): string {
    return `<button data-route="${route}" class="${active === route ? "is-active" : ""}" aria-current="${active === route ? "page" : "false"}"><i>${uiIcon(route)}</i><span>${label}</span></button>`;
  }

  private showToast(message: string): void {
    const toast = this.root.querySelector("#toast");
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add("is-visible");
    window.setTimeout(() => toast.classList.remove("is-visible"), 1600);
  }

  private registerServiceWorker(): void {
    if (!("serviceWorker" in navigator)) return;
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // Online play remains available if service-worker registration is blocked.
      });
    });
  }
}
